// MovimientosTab.js
import React, { useEffect, useState, useMemo, useRef, useCallback } from "react";
import {
    Paper, Table, TableHead, TableRow, TableCell, TableBody,
    Button, IconButton, Box, Tooltip,
    Dialog, DialogTitle, DialogContent, DialogActions, Snackbar,
    CircularProgress, TableSortLabel, TextField, Grid, Chip,
    Divider, Typography, Collapse, TablePagination, MenuItem
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import LinkIcon from "@mui/icons-material/Link";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { Alert, AlertTitle, Skeleton } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import makeStyles from '@mui/styles/makeStyles';
import InputAdornment from "@mui/material/InputAdornment";
import SearchIcon from "@mui/icons-material/Search";
import {
    expedienteMovimientosJWT,
    crearMovimientoJWT,
    editarMovimientoJWT,
    eliminarMovimientoJWT,
    getCombosJWT,
    movimientoPdfJWT,
    escritosComboJWT,
    expedienteDetalleJWT,
    expedientePartesJWT,
    expedienteRadicacionJWT,
    panelFirmaSolicitarJWT,
    downloadVinculadoJWT,
    getVinculadoJWT,
    expedienteVinculadosJWT,
    embargoByMovimientoJWT,
    bandejaSalidaAgregarJWT,
    documentosSaveJWT,
} from "../../api/endpoints";
import usePermisosModulo from "../../hooks/usePermisosModulo";
import TinyEditor from "../../components/TinyEditor";
import DescriptionIcon from "@mui/icons-material/Description";
import AssignmentIcon from "@mui/icons-material/Assignment";
import BookmarkIcon from "@mui/icons-material/Bookmark";
import GavelIcon from "@mui/icons-material/Gavel";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import PersonIcon from "@mui/icons-material/Person";
import FolderIcon from "@mui/icons-material/Folder";
import OutboxIcon from "@mui/icons-material/Outbox";
import ReceiptIcon from "@mui/icons-material/Receipt";
import TimelineIcon from "@mui/icons-material/Timeline";
import ViewListIcon from "@mui/icons-material/ViewList";
import FilterListIcon from "@mui/icons-material/FilterList";
import GetAppIcon from "@mui/icons-material/GetApp";
import PrintIcon from "@mui/icons-material/Print";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import ImageIcon from "@mui/icons-material/Image";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import logoSIGIF from "../../assets/sigifblue.png";
import { descargarHtmlComoPdf } from "../../utils/descargarPdf";
import EventIcon from "@mui/icons-material/Event";
import BarChartIcon from "@mui/icons-material/BarChart";
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import 'moment/locale/es';
import 'react-big-calendar/lib/css/react-big-calendar.css';

moment.locale('es');
const localizer = momentLocalizer(moment);

// ⭐ Constante global para cache (5 minutos)
const CACHE_TTL = 5 * 60 * 1000;

// Normaliza claves de objetos del backend (CAPITALIZADAS) a lowercase
function normalizeKeys(obj) {
    if (!obj || typeof obj !== 'object') return obj;
    if (Array.isArray(obj)) return obj.map(normalizeKeys);
    const out = {};
    for (const [k, v] of Object.entries(obj)) {
        out[k.toLowerCase()] = v;
    }
    return out;
}

// tipoExpediente: 1 = Judicial, 2 = Administrativo
function resolverTipoMovimiento(tipoUI, escritoObj, tipoExpediente) {
    const base = (tipoUI || "").trim();
    const baseKey = keyify(base);

    // 1) Si NO es "MIS ESCRITOS", devolvemos lo que vino
    if (baseKey !== keyify("MIS ESCRITOS")) return base;

    // 2) Si tenemos categoría con tipo sugerido desde la API, usarlo
    if (escritoObj) {
        const sugerido =
            escritoObj.Tipo_Movimiento_Sugerido ||
            escritoObj.tipo_movimiento_sugerido ||
            escritoObj.tipoMovimientoSugerido ||
            "";

        if (sugerido) return sugerido;
    }

    // 3) Fallback por tipo de expediente
    const te = Number(tipoExpediente || 1);
    if (te === 1) return "PROCESAL";  // Judicial
    if (te === 2) return "OTRO MOV";  // Administrativo
    return "PROCESAL";
}

const useStyles = makeStyles(() => ({
    headerBar: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        marginBottom: 12,
        gap: 12
    },
    headerLeft: {
        display: "flex",
        alignItems: "center",
        gap: 12,
        flexWrap: "wrap"
    },
    searchInput: {
        width: 360,
        maxWidth: "100%"
    }
}));

//const U = s => (s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").trim().toUpperCase();

function plainText(html) {
    const div = document.createElement("div");
    div.innerHTML = html || "";
    return div.textContent || div.innerText || "";
}

function parseFechaAny(v) {
    if (!v) return null;
    const s = String(v).trim();

    // ⭐ YYYY-MM-DD sin hora → interpretar como fecha LOCAL (no UTC)
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
        const [yyyy, mm, dd] = s.split('-').map(Number);
        return new Date(yyyy, mm - 1, dd); // Constructor local
    }

    // ISO o 'YYYY-MM-DD HH:mm:ss'
    if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
        const iso = s.replace(" ", "T");
        const d = new Date(iso);
        return isNaN(d) ? null : d;
    }

    // 'dd/MM/yyyy' con hora opcional
    const m = s.match(
        /^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:\s+(\d{1,2}):(\d{2})(?::(\d{2}))?)?$/
    );
    if (m) {
        const dd = m[1].padStart(2, "0");
        const mm = m[2].padStart(2, "0");
        const yyyy = m[3];
        const hh = (m[4] || "00").padStart(2, "0");
        const mi = (m[5] || "00").padStart(2, "0");
        const ss = (m[6] || "00").padStart(2, "0");
        const iso = `${yyyy}-${mm}-${dd}T${hh}:${mi}:${ss}`;
        const d = new Date(iso);
        return isNaN(d) ? null : d;
    }

    // fallback
    const d = new Date(s);
    return isNaN(d) ? null : d;
}

function formatFecha(fechaStr) {
    if (!fechaStr) return "";
    if (/^\d{4}-\d{2}-\d{2}/.test(fechaStr)) {
        const [a, m, d] = fechaStr.split("T")[0].split("-");
        return `${d.padStart(2, "0")}/${m.padStart(2, "0")}/${a}`;
    }
    if (/^\d{1,2}\/\d{1,2}\/\d{4}/.test(fechaStr)) {
        const partes = fechaStr.split(" ")[0].split("/");
        return `${partes[0].padStart(2, "0")}/${partes[1].padStart(2, "0")}/${partes[2]}`;
    }
    if (/^\d{4}-\d{2}-\d{2}/.test(fechaStr)) {
        const [a, m, d] = fechaStr.split(" ")[0].split("-");
        return `${d.padStart(2, "0")}/${m.padStart(2, "0")}/${a}`;
    }
    const fecha = new Date(fechaStr);
    if (!isNaN(fecha)) return fecha.toLocaleDateString("es-AR");
    return fechaStr;
}

// ----- API helpers -----
// api_generar_escrito.aspx -- NO JWT equivalent yet, graceful degradation
async function obtenerEscritoGenerado(escritoId, idExpediente) {
    try {
        const token = localStorage.getItem("SIGIF_JWT_TOKEN");
        if (!token) return '';
        const { nodeApiPath } = await import("../../utils/paths");
        const res = await fetch(nodeApiPath(`escritos/${escritoId}`), {
            headers: { "Accept": "application/json", "Authorization": `Bearer ${token}` },
        });
        const data = await res.json();
        return data?.data?.Contenido || data?.data?.contenido || '';
    } catch {
        return '';
    }
}

// Etapas de proceso via JWT combos
async function fetchEstadoActual(idExpediente) {
    try {
        const data = await expedienteDetalleJWT(idExpediente);
        const exp = data?.expediente || data;
        const idEtapa = exp?.id_etapa_procesal || exp?.idEtapaProcesal || exp?.Id_Etapa_Procesal;
        if (!idEtapa) return null;
        const combo = await getCombosJWT('etapas_proceso', '', 200);
        const items = combo?.items || combo?.estados || [];
        const found = items.find(e => Number(e.id ?? e.Id) === Number(idEtapa));
        if (!found) return { id: idEtapa, nombre: `Etapa ${idEtapa}` };
        return { id: found.id ?? found.Id, nombre: found.nombre ?? found.Nombre ?? found.label ?? found.Etapa ?? "" };
    } catch { return null; }
}

async function fetchEstadosDisponibles(tipoExpediente) {
    try {
        const data = await getCombosJWT('etapas_proceso', '', 200);
        const items = data?.items || data?.estados || [];
        // Normalizar a {id, nombre} para que optLabel funcione correctamente
        return items.map(o => ({
            id: o.id ?? o.Id,
            nombre: o.nombre ?? o.Nombre ?? o.label ?? o.Etapa ?? o.Estado ?? ""
        }));
    } catch { return []; }
}

// --- Estado del Proceso (Movimientos) — via JWT ---
const ETAPAS_API = {
    listar: async () => {
        try {
            const data = await getCombosJWT('etapas_proceso', '', 200);
            return { estados: normalizeKeys(data?.items || data?.estados || []) };
        } catch { return { estados: [] }; }
    },
    actual: async (idExpediente) => {
        try {
            const data = await expedienteDetalleJWT(idExpediente);
            const exp = data?.expediente || data;
            const idEtapa = exp?.id_etapa_procesal || exp?.idEtapaProcesal || exp?.Id_Etapa_Procesal;
            if (!idEtapa) return { estado: null };
            // Buscar nombre en combo de etapas
            const combo = await getCombosJWT('etapas_proceso', '', 200);
            const items = combo?.items || combo?.estados || [];
            const found = items.find(e => Number(e.id ?? e.Id) === Number(idEtapa));
            return { estado: found || { id: idEtapa, label: `Etapa ${idEtapa}` } };
        } catch { return { estado: null }; }
    },
    cambiar: async (idExpediente, estado, estadoAnterior) => {
        try {
            const nombreAnterior = estadoAnterior?.nombre || estadoAnterior?.label || 'Sin asignar';
            const nombreNuevo = estado?.nombre || estado?.Nombre || estado?.label || '';
            const movimiento = {
                tipo: 'ESTADO PROCESO',
                descripcion: `Etapa: ${nombreAnterior} → ${nombreNuevo}`,
                fecha: new Date().toISOString(),
                idEstado: estado?.id,
                responsable: localStorage.getItem('usuario') || '',
            };
            const res = await crearMovimientoJWT(idExpediente, movimiento);
            return res;
        } catch (e) {
            return { ok: false, mensaje: e.message };
        }
    }
};

// Normaliza: sin acentos, uppper, sin espacios de más
const normKey = (s) =>
    String(s || "")
        .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
        .toUpperCase().trim();

const keyify = (s) => normKey(s).replace(/\s+/g, ""); // sin espacios

// ¿El tipo DB coincide con alguno de estos alias?
const isTipo = (dbTipo, ...aliases) => {
    const k = keyify(dbTipo);
    return aliases.some(a => k === keyify(a));
};

// Para mostrar "Ver" solo si hay escrito real
const hasEscrito = (s) => !!String(s || "").trim();

// Label visibles en UI (sin tocar DB)
const displayTipoLabel = (dbTipo) => {
    if (isTipo(dbTipo, "ADJUNTO BASE-64")) return "Carga Automática";
    if (isTipo(dbTipo, "OTRO MOV")) return "Mov. Administrativo";
    if (isTipo(dbTipo, "MIS ESCRITOS")) return "Mis Escritos";
    if (isTipo(dbTipo, "RECIBOS")) return "Recibos";
    if (isTipo(dbTipo, "ESTADO PROCESO")) return "Estado Procesal";
    if (isTipo(dbTipo, "PROCESAL")) return "Procesal";
    if (isTipo(dbTipo, "EXTRA PROCESAL")) return "Extra Procesal";
    if (isTipo(dbTipo, "CEDULA")) return "Cédula";
    if (isTipo(dbTipo, "CARTA PODER")) return "Carta Poder";
    if (isTipo(dbTipo, "GENERADOR DE CARTA PODER")) return "Generador de Carta Poder";
    if (isTipo(dbTipo, "REFERENCIA")) return "Referencia";
    if (isTipo(dbTipo, "OFIGEN")) return "Ofigen";
    if (isTipo(dbTipo, "OFICINA DE PAGOS")) return "Oficina de Pagos";
    if (isTipo(dbTipo, "VINCULADOS")) return "Vinculados";
    if (isTipo(dbTipo, "EXPEDIENTE ELECTRONICO")) return "Expediente Electrónico";
    if (isTipo(dbTipo, "FORMULARIOS")) return "Formularios";
    if (isTipo(dbTipo, "REGISTRO CANCELADO")) return "Registro Cancelado";
    if (isTipo(dbTipo, "REQUERIMIENTOS")) return "Requerimientos";
    if (isTipo(dbTipo, "EMBARGO")) return "Embargo";
    return dbTipo || "";
};

// ¿Es tipo Embargo?
const isEmbargo = (dbTipo) => isTipo(dbTipo, "EMBARGO");

// Opciones de estado de embargo
const EMBARGO_ESTADOS = ["Diligenciado", "Trabado", "Libramiento de fondos", "Levantamiento"];

// ¿Requiere escrito?
const tipoReqEscrito = (dbTipo) => {
    return (
        isTipo(dbTipo, "PROCESAL") ||
        isTipo(dbTipo, "EXTRA PROCESAL") ||
        isTipo(dbTipo, "OFIGEN") ||
        isTipo(dbTipo, "OFICINA DE PAGOS") ||
        isTipo(dbTipo, "GENERADOR DE CARTA PODER") ||
        isTipo(dbTipo, "MIS ESCRITOS") ||
        isTipo(dbTipo, "RECIBOS")
    );
};

// ¿Requiere PDF adjunto?
const tipoReqPdf = (dbTipo) => {
    return isTipo(dbTipo, "CARTA PODER") || isTipo(dbTipo, "CEDULA", "CÉDULA");
};

// ===== Variables: parseo, filtros y reemplazo =====
const VAR_RE = /\{([A-Za-z0-9_]+)(?:\|([A-Za-z0-9_|:./-]+))?\}/g;

function applyFilters(val, filterStr) {
    if (val == null) return '';
    let out = String(val);
    if (!filterStr) return out;
    for (const f of String(filterStr).split('|')) {
        if (!f) continue;
        if (f === 'upper') out = out.toUpperCase();
        else if (f === 'lower') out = out.toLowerCase();
        else if (f === 'title') out = out.replace(/\w\S*/g, w => w[0].toUpperCase() + w.slice(1).toLowerCase());
        else if (f.startsWith('date:')) {
            const fmt = f.slice(5);
            const d = new Date(out);
            if (!isNaN(d)) {
                out = fmt
                    .replace(/dd/g, String(d.getDate()).padStart(2, '0'))
                    .replace(/MM/g, String(d.getMonth() + 1).padStart(2, '0'))
                    .replace(/yyyy/g, String(d.getFullYear()));
            }
        }
    }
    return out;
}

// ============================================================================
// Historial local de descripciones (autocomplete del campo Descripción).
// Persiste en localStorage por usuario en este navegador. Máximo 50 entradas,
// dedup case-insensitive, las más recientes primero.
// ============================================================================
const HIST_DESC_KEY = 'SIGIF_MOVS_DESCRIPCIONES';
const HIST_DESC_MAX = 50;

function loadDescripcionesHistorial() {
    try {
        const raw = localStorage.getItem(HIST_DESC_KEY);
        if (!raw) return [];
        const arr = JSON.parse(raw);
        return Array.isArray(arr) ? arr.filter(x => typeof x === 'string' && x.trim()) : [];
    } catch { return []; }
}

function addDescripcionHistorial(desc) {
    if (!desc || typeof desc !== 'string') return;
    const limpia = desc.trim();
    if (limpia.length < 3) return; // descartar fragmentos cortos sin valor
    try {
        const prev = loadDescripcionesHistorial();
        // Dedup case-insensitive: si ya existe una entrada equivalente, la quitamos
        // antes de meter la nueva al inicio.
        const lower = limpia.toLowerCase();
        const filtered = prev.filter(x => x.toLowerCase() !== lower);
        filtered.unshift(limpia);
        const truncated = filtered.slice(0, HIST_DESC_MAX);
        localStorage.setItem(HIST_DESC_KEY, JSON.stringify(truncated));
    } catch { /* localStorage puede estar lleno o bloqueado */ }
}

function replaceVarsInHtml(html, vars) {
    if (!html) return { html, missing: [] };
    // Crear lookup case-insensitive (UPPERCASE keys) como el sistema viejo
    const upperVars = {};
    if (vars) {
        for (const [k, v] of Object.entries(vars)) {
            upperVars[k.toUpperCase()] = v;
        }
    }
    const missing = new Set();
    const out = String(html).replace(VAR_RE, (_, key, filters) => {
        const upperKey = key.toUpperCase();
        const has = Object.prototype.hasOwnProperty.call(upperVars, upperKey);
        if (!has || upperVars[upperKey] === '' || upperVars[upperKey] == null) { missing.add(key); return `{${key}}`; }
        return applyFilters(upperVars[upperKey], filters);
    });
    return { html: out, missing: Array.from(missing) };
}

function extractVars(html) {
    const keys = new Set();
    String(html).replace(VAR_RE, (_, k) => { keys.add(String(k)); return ''; });
    return Array.from(keys);
}

const norm = (s) => String(s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

// Función para convertir logo a base64
const getBase64FromImage = (imgPath, callback) => {
    const img = new window.Image();
    img.crossOrigin = "Anonymous";
    img.src = imgPath;
    img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0);
        const dataURL = canvas.toDataURL("image/png");
        callback(dataURL);
    };
};

// Función para limpiar detalles en el PDF
function limpiarDetalleParaPDF(detalle) {
    if (!detalle) return "";

    // Normalización básica
    let limpio = detalle.replace(/\r?\n/g, " ").replace(/\s+/g, " ").trim();

    // Multi-campo: detecta múltiples comparaciones separadas por |
    if (limpio.includes("→") && limpio.includes(":") && limpio.includes("'")) {
        const partes = limpio.split(/\s*\|\s*/);
        const result = partes.map(p => {
            const reg = /^([\wÁÉÍÓÚÜÑáéíóúüñ]+): ['"""'']?(.+?)['"""'']? ?→ ?['"""'']?(.+?)['"""'']?$/;
            const m = p.match(reg);
            if (m) return `${m[1]} anterior: "${m[2]}", ${m[1]} actual: "${m[3]}"`;
            return p;
        });
        return result.join('\n');
    }

    // Caso universal: Campo: 'A' → 'B'
    const regexCambioUniversal = /^([\wÁÉÍÓÚÜÑáéíóúüñ]+): ['"""'']?(.+?)['"""'']? ?→ ?['"""'']?(.+?)['"""'']?$/;
    const matchUniversal = limpio.match(regexCambioUniversal);
    if (matchUniversal) {
        return `${matchUniversal[1]} anterior: "${matchUniversal[2]}", ${matchUniversal[1]} actual: "${matchUniversal[3]}"`;
    }

    // JSON parse
    if (typeof detalle === "string" && limpio.startsWith("{")) {
        try {
            const obj = JSON.parse(limpio);
            let campos = [];
            if (obj.tipo) campos.push(`Tipo: ${obj.tipo}`);
            if (obj.descripcion) campos.push(`Descripción: ${obj.descripcion}`);
            if (obj.fecha) campos.push(`Fecha: ${obj.fecha}`);
            if (obj.responsable) campos.push(`Responsable: ${obj.responsable}`);
            return campos.join("\n");
        } catch (e) {
            return limpio;
        }
    }

    return limpio;
}

// Highlight de texto en búsqueda
function highlightText(text, query) {
    if (!query || !text) return text;

    const plainTxt = String(text);
    const nq = norm(query);
    const nt = norm(plainTxt);

    const idx = nt.indexOf(nq);
    if (idx === -1) return plainTxt;

    // Encontrar la posición real en el texto original (con acentos)
    let realIdx = idx;
    let normalizedCount = 0;
    for (let i = 0; i < plainTxt.length; i++) {
        if (normalizedCount === idx) {
            realIdx = i;
            break;
        }
        if (norm(plainTxt[i]).length > 0) normalizedCount++;
    }

    const before = plainTxt.slice(0, realIdx);
    const match = plainTxt.slice(realIdx, realIdx + query.length);
    const after = plainTxt.slice(realIdx + query.length);

    return (
        <>
            {before}
            <span style={{ background: '#fff59d', fontWeight: 700, padding: '2px 4px', borderRadius: 4 }}>
                {match}
            </span>
            {after}
        </>
    );
}

// ===== Expediente -> glosario base =====
// Obtiene detalle del expediente + partes en paralelo para armar el glosario de variables
/**
 * Nombre en criollo de cada variable, y en qué pantalla se carga el dato.
 * Un `{DEMANDADO_NN}` en medio de un escrito judicial parece un error del sistema;
 * en realidad quiere decir que al expediente le falta ese dato. Esto traduce la
 * variable a algo accionable para el abogado.
 */
const ORIGEN_VARIABLES = {
    DEMANDADO_NN: ['Demandado', 'Partes'],
    DEMANDADO: ['Demandado', 'Partes'],
    DEMANDADO_DIRECCION: ['Domicilio del demandado', 'Partes'],
    DEMANDADO_LOCALIDAD: ['Localidad del demandado', 'Partes'],
    DEMANDADO_DNI: ['DNI del demandado', 'Partes'],
    ABOGADO: ['Abogado apoderado', 'Partes'],
    ABOGADOS: ['Abogado apoderado', 'Partes'],
    ABOGADOS_DAP: ['Abogado apoderado', 'Partes'],
    ABOGADO_FEDERAL: ['Abogado apoderado (matrícula federal)', 'Partes'],
    ABOGADOS_FEDERAL: ['Abogado apoderado (matrícula federal)', 'Partes'],
    ABOGADO_DOMICILIO: ['Domicilio del abogado', 'Partes'],
    ABOGADO_CONTRAPARTE: ['Abogado de la contraparte', 'Partes'],
    ABOGADOS_CONTRAPARTE: ['Abogado de la contraparte', 'Partes'],
    APODERADO: ['Apoderado', 'Partes'],
    CARTA_PODER: ['Carta poder', 'Partes'],
    ACTOR: ['Parte actora', 'Partes'],
    JUZGADO: ['Juzgado', 'Radicación'],
    SECRETARIA: ['Secretaría', 'Radicación'],
    CAPITAL_RECLAMADO: ['Capital reclamado', 'Radicación'],
    CAPITAL_RECLAMADO_LETRAS: ['Capital reclamado', 'Radicación'],
    MONTO_ACTUALIZADO: ['Monto actualizado', 'Radicación'],
    CARATULA: ['Carátula', 'datos del expediente'],
    AUTOS: ['Autos', 'datos del expediente'],
};

/**
 * Agrupa las variables faltantes por dato y por pantalla, sin repetir.
 * Las que no están en ORIGEN_VARIABLES se listan igual con su nombre crudo: es feo,
 * pero esconderlas sería peor — el escrito va a tener ese hueco sí o sí y el usuario
 * tiene que enterarse. Si alguna aparece seguido, se agrega al diccionario.
 */
function describirFaltantes(faltantes) {
    const porPantalla = new Map();
    const sinMapear = new Set();
    for (const v of faltantes || []) {
        const [etiqueta, pantalla] = ORIGEN_VARIABLES[String(v).toUpperCase()] || [null, null];
        if (!etiqueta) { sinMapear.add(String(v)); continue; }
        if (!porPantalla.has(pantalla)) porPantalla.set(pantalla, new Set());
        porPantalla.get(pantalla).add(etiqueta);
    }
    const grupos = Array.from(porPantalla.entries())
        .map(([pantalla, datos]) => ({ pantalla, datos: Array.from(datos) }));
    if (sinMapear.size) grupos.push({ pantalla: null, datos: Array.from(sinMapear) });
    return grupos;
}

/**
 * Trae los datos del expediente para armar el glosario de variables.
 *
 * Sin caché a propósito. Antes guardaba el resultado 2 minutos por expediente y no
 * se invalidaba nunca, ni siquiera al editar las partes: cargabas el demandado o el
 * abogado, volvías a generar el escrito dentro de esa ventana y salía con
 * {DEMANDADO_NN} y {ABOGADOS} igual, usando el glosario viejo. Son tres llamadas y
 * solo se hacen al generar un escrito judicial; que el dato esté fresco vale más.
 */
async function fetchExpedienteVarsFlexible(id) {
    if (!id) return {};

    try {
        // Llamar detalle + partes + radicacion en paralelo
        const [detalleRes, partesRes, radicacionRes] = await Promise.all([
            expedienteDetalleJWT(id),
            expedientePartesJWT(id),
            expedienteRadicacionJWT(id).catch(() => ({})),
        ]);

        const exp = detalleRes?.expediente || {};
        // Partes viene flat: { ok, provincia, contraparte, auxiliares }
        const partes = partesRes?.partes || {
            provincia: partesRes?.provincia || [],
            contraparte: partesRes?.contraparte || [],
            auxiliares: partesRes?.auxiliares || [],
        };
        const radicacion = radicacionRes?.radicacion || radicacionRes || {};

        // Unificar en un solo objeto
        return { ...exp, partes, radicacion };
    } catch (error) {
        console.error('[VARS] Error obteniendo datos del expediente:', error);
        return {};
    }
}

// Colores por estado
const getEstadoColor = (nombreEstado) => {
    const nombre = (nombreEstado || "").toLowerCase();

    // Estados iniciales/abiertos - Azul
    if (nombre.includes("iniciado") || nombre.includes("abierto") || nombre.includes("ingreso") || nombre.includes("demanda presentada")) {
        return { bg: "linear-gradient(90deg, #3381a3 0%, #1d7ba3 100%)", text: "#fff" };
    }

    // Estados en progreso - Naranja
    if (nombre.includes("proceso") || nombre.includes("trámite") || nombre.includes("pendiente") || nombre.includes("en curso")) {
        return { bg: "linear-gradient(90deg, #f57c00 0%, #ef6c00 100%)", text: "#fff" };
    }

    // Estados suspendidos/archivados - Gris
    if (nombre.includes("suspendido") || nombre.includes("archivado") || nombre.includes("paralizado") || nombre.includes("inactivo")) {
        return { bg: "linear-gradient(90deg, #757575 0%, #616161 100%)", text: "#fff" };
    }

    // Estados finalizados exitosos - Verde
    if (nombre.includes("finalizado") || nombre.includes("resuelto") || nombre.includes("concluido") || nombre.includes("sentencia")) {
        return { bg: "linear-gradient(90deg, #388e3c 0%, #2e7d32 100%)", text: "#fff" };
    }

    // Estados rechazados/negativos - Rojo
    if (nombre.includes("rechazado") || nombre.includes("desestimado") || nombre.includes("anulado")) {
        return { bg: "linear-gradient(90deg, #d32f2f 0%, #c62828 100%)", text: "#fff" };
    }

    // Default - Azul
    //return { bg: "linear-gradient(90deg, #1976d2 0%, #1565c0 100%)", text: "#fff" };
    return { bg: "linear-gradient(90deg, #3381a3 0%, #1d7ba3 100%)", text: "#fff" };
};

// Convertir número a texto en español (para CAPITAL_RECLAMADO_LETRAS)
function numeroALetras(n) {
    if (n == null || isNaN(n) || n === 0) return '';
    const UNIDADES = ['', 'uno', 'dos', 'tres', 'cuatro', 'cinco', 'seis', 'siete', 'ocho', 'nueve',
        'diez', 'once', 'doce', 'trece', 'catorce', 'quince', 'dieciséis', 'diecisiete', 'dieciocho', 'diecinueve', 'veinte'];
    const DECENAS = ['', '', 'veinti', 'treinta', 'cuarenta', 'cincuenta', 'sesenta', 'setenta', 'ochenta', 'noventa'];
    const CENTENAS = ['', 'ciento', 'doscientos', 'trescientos', 'cuatrocientos', 'quinientos', 'seiscientos', 'setecientos', 'ochocientos', 'novecientos'];

    function seccion(num) {
        if (num === 0) return '';
        if (num <= 20) return UNIDADES[num];
        if (num < 30) return 'veinti' + UNIDADES[num - 20];
        if (num < 100) {
            const d = Math.floor(num / 10), u = num % 10;
            return DECENAS[d] + (u ? ' y ' + UNIDADES[u] : '');
        }
        if (num === 100) return 'cien';
        if (num < 1000) {
            const c = Math.floor(num / 100), resto = num % 100;
            return CENTENAS[c] + (resto ? ' ' + seccion(resto) : '');
        }
        if (num < 2000) return 'mil ' + seccion(num - 1000);
        if (num < 1000000) {
            const miles = Math.floor(num / 1000), resto = num % 1000;
            return seccion(miles) + ' mil' + (resto ? ' ' + seccion(resto) : '');
        }
        if (num < 2000000) return 'un millón' + (num % 1000000 ? ' ' + seccion(num % 1000000) : '');
        const millones = Math.floor(num / 1000000), resto = num % 1000000;
        return seccion(millones) + ' millones' + (resto ? ' ' + seccion(resto) : '');
    }

    const valor = Math.abs(parseFloat(n));
    const entero = Math.floor(valor);
    const centavos = Math.round((valor - entero) * 100);
    let texto = seccion(entero);
    if (centavos > 0) texto += ' con ' + seccion(centavos) + '/100';
    else texto += ' con 00/100';
    return texto.charAt(0).toUpperCase() + texto.slice(1);
}

// Normalizá nombres que usan tus plantillas {Autos}, {Caratula}, etc.
function buildGlossaryFromExpediente(raw, movimientoData = {}) {
    const g = {};

    // FECHA: Siempre la fecha actual en formato DD/MM/YYYY
    const hoy = new Date();
    g.FECHA = hoy.toLocaleDateString('es-AR');

    // FECHA_ISO: Fecha actual en formato ISO (YYYY-MM-DD)
    const year = hoy.getFullYear();
    const month = String(hoy.getMonth() + 1).padStart(2, '0');
    const day = String(hoy.getDate()).padStart(2, '0');
    g.FECHA_ISO = `${year}-${month}-${day}`;

    // FECHA_LETRAS: Fecha actual en formato de letras
    const meses = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
                   'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
    g.FECHA_LETRAS = `${day} de ${meses[hoy.getMonth()]} de ${year}`;

    // Componentes de fecha individuales (usados en Carta Poder / Readjudicación)
    g.DIA = day;
    g.MES = meses[hoy.getMonth()].toUpperCase();
    g.ANIO = String(year);
    g.FECHA_MES_ANIO = `${day} de ${meses[hoy.getMonth()].toUpperCase()} ${year}`;

    // ID del expediente
    g.EXPEDIENTE = raw?.id ? String(raw.id) : '';

    // Expedientes Judiciales
    g.AUTOS = raw?.autos_1_instancia ?? raw?.Autos_1_Instancia ?? '';
    g.AUTOS_1_INSTANCIA = raw?.autos_1_instancia ?? raw?.Autos_1_Instancia ?? '';
    g.AUTOS_2_INSTANCIA = raw?.autos_2_instancia ?? raw?.Autos_2_Instancia ?? '';
    g.AUTOS_EXTRA = raw?.autos_extra ?? raw?.Autos_Extra ?? '';
    g.AUTOS_EXTRA_FED = raw?.autos_extra_fed ?? raw?.Autos_Extra_Fed ?? '';
    g.CARATULA = raw?.caratula ?? raw?.Caratula ?? '';
    g.TIPO_PROCESO = raw?.tipo_proceso ?? raw?.Tipo_Proceso ?? '';
    g.OBJETO_RECLAMO = raw?.objeto_reclamo ?? raw?.Objeto_Reclamo ?? '';
    g.ESTADO_PROCESO = raw?.estado_proceso ?? raw?.Estado_Proceso ?? '';
    g.TRAMITE = raw?.tramite ?? raw?.Tramite ?? '';

    // Expedientes Administrativos
    g.NRO_EXPEDIENTE = raw?.nro_expediente ?? raw?.expediente_adm ?? raw?.Expediente_Adm ?? '';
    g.EXPEDIENTE_ADM = raw?.nro_expediente ?? raw?.expediente_adm ?? raw?.Expediente_Adm ?? '';
    g.INICIADOR = raw?.iniciador ?? raw?.Iniciador ?? '';
    g.EXTRACTO = raw?.extracto ?? raw?.Extracto ?? '';
    g.ESTADO = raw?.estado ?? raw?.Estado ?? raw?.estado_proceso ?? '';
    g.REPARTICION_ORIGEN = raw?.reparticion_origen ?? raw?.Reparticion_Origen ?? '';

    // Fechas
    g.FECHA_INICIO = raw?.fecha_inicio ?? raw?.Fecha_Inicio ?? '';
    g.FECHA_ASIGNACION = raw?.fecha_asignacion ?? raw?.Fecha_Asignacion ?? '';
    g.FECHA_SENTENCIA_1_INS = raw?.fecha_sentencia_1_ins ?? raw?.Fecha_Sentencia_1_Ins ?? '';
    g.FECHA_SENTENCIA_2_INS = raw?.fecha_sentencia_2_ins ?? raw?.Fecha_Sentencia_2_Ins ?? '';

    // Módulo
    g.MODULO = raw?.modulo ?? raw?.Modulo ?? '';

    // Montos y resultados
    g.MONTO_ACTUALIZADO = raw?.monto_actualizado_demanda ?? raw?.Monto_Actualizado_Demanda ?? '';
    g.RESULTADO_1 = raw?.resultado1 ?? raw?.Resultado1 ?? '';
    g.RESULTADO_2 = raw?.resultado2 ?? raw?.Resultado2 ?? '';

    // Ubicación
    g.UBICACION_OFICINA = raw?.ubicacion_oficina ?? raw?.Ubicacion_Oficina ?? '';
    g.CANTIDAD_CUERPOS = raw?.cantidad_cuerpos ?? raw?.Cantidad_Cuerpos ?? '';
    g.CANTIDAD_FOJAS = raw?.cantidad_fojas ?? raw?.Cantidad_Fojas ?? '';

    // Abogados participantes
    g.ABOGADOS_PARTICIPANTES = raw?.abogados_participantes ?? raw?.Abogados_Participantes ?? '';

    // ===== Procesar PARTES del expediente =====
    const partes = raw?.partes;

    // Buscar DEMANDADO en Contraparte (grilla 2)
    const demandado = partes?.contraparte?.find(p =>
        (p.caracter || '').toLowerCase().includes('demandad')
    );
    g.DEMANDADO = demandado?.persona || '';
    g.DEMANDADO_NN = demandado?.persona || '';
    g.DEMANDADO_DIRECCION = demandado?.direccion || '';
    g.DEMANDADO_DNI = demandado?.dni || '';

    // Buscar ACTOR en Provincia (grilla 1) - Provincia Actora
    const actor = partes?.provincia?.find(p =>
        (p.caracter || '').toLowerCase().includes('actora') ||
        (p.caracter || '').toLowerCase().includes('actor')
    );
    g.ACTOR = actor?.persona || 'PROVINCIA DE SAN JUAN';

    // ===== ABOGADOS / DIRECTOR / CONTRAPARTE =====
    // Mismo criterio y formato que el backend (documentos.repository.ts) para que
    // el preview de movimientos coincida con el editor de Documentos.
    const provincia = partes?.provincia || [];
    const idCar = (p) => Number(p.id_caracter ?? p.Id_Caracter);
    const matFedStr = (p) => {
        // Convención histórica (igual que backend y sistema viejo): la columna
        // Matricula_Federal_Tomo guarda el FOLIO y la Folio guarda el TOMO. Se
        // leen CRUZADAS para mostrar siempre "Tomo X Folio Y" correcto.
        const tomo = String(p.matricula_federal_folio ?? p.Matricula_Federal_Folio ?? '').trim();
        const folio = String(p.matricula_federal_tomo ?? p.Matricula_Federal_Tomo ?? '').trim();
        if (tomo && folio) return `Tomo ${tomo} Folio ${folio}`;
        if (tomo) return `Tomo ${tomo}`;
        if (folio) return `Folio ${folio}`;
        return '';
    };
    const fmtProv = (p) => {
        const nom = (p.persona || '').trim();
        if (!nom) return '';
        const mat = (p.matricula || '').trim();
        return mat ? `${nom} Mat. ${mat}` : nom;
    };
    const fmtFed = (p) => {
        const nom = (p.persona || '').trim();
        if (!nom) return '';
        const fed = matFedStr(p);
        return fed ? `${nom} - Mat. Fed. ${fed}` : nom;
    };
    // Abogados apoderados de la Provincia = caracter 3, 8, 26 (igual que el backend)
    const abogadosArr = provincia.filter(p => [3, 8, 26].includes(idCar(p)));
    g.ABOGADO = abogadosArr.length ? fmtProv(abogadosArr[0]) : '';
    g.ABOGADOS = abogadosArr.map(fmtProv).filter(Boolean).join('; ');
    g.ABOGADOS_DAP = g.ABOGADOS;
    g.ABOGADO_FEDERAL = abogadosArr.length ? fmtFed(abogadosArr[0]) : '';
    g.ABOGADOS_FEDERAL = abogadosArr.map(fmtFed).filter(Boolean).join('; ');
    g.ABOGADO_DOMICILIO = abogadosArr[0]?.direccion || '';

    // Director / Subdirector (provincia, todas las variantes menos "EX ...")
    const normCar = (s) => (s || '').toUpperCase().trim();
    const esSub = (cn) => cn.startsWith('SUBDIRECTOR') || cn.startsWith('SUB-DIRECTOR') || cn.startsWith('SUB DIRECTOR');
    const fmtDir = (p) => {
        const nom = (p.persona || '').trim();
        if (!nom) return '';
        const mat = (p.matricula || '').trim();
        return mat ? `${nom} - Mat. Nº ${mat}` : nom;
    };
    // Variante con matrícula FEDERAL del director/subdirector.
    const fmtDirFed = (p) => {
        const nom = (p.persona || '').trim();
        if (!nom) return '';
        const fed = matFedStr(p);
        return fed ? `${nom} - Mat. Fed. ${fed}` : nom;
    };
    const dirArr = provincia.filter(p => {
        const cn = normCar(p.caracter);
        return !cn.startsWith('EX ') && (cn.startsWith('DIRECTOR') || esSub(cn));
    });
    const dirP = dirArr.find(p => { const cn = normCar(p.caracter); return !esSub(cn) && cn.startsWith('DIRECTOR'); });
    const subP = dirArr.find(p => esSub(normCar(p.caracter)));
    g.DIRECTOR = dirP ? fmtDir(dirP) : '';
    g.SUBDIRECTOR = subP ? fmtDir(subP) : '';
    g.DIRECTOR_FEDERAL = dirP ? fmtDirFed(dirP) : '';
    g.SUBDIRECTOR_FEDERAL = subP ? fmtDirFed(subP) : '';

    // Abogados de la contraparte (grilla 2/3, caracter 3/8/26)
    const contraArr = [...(partes?.contraparte || []), ...(partes?.auxiliares || [])]
        .filter(p => [3, 8, 26].includes(idCar(p)));
    g.ABOGADO_CONTRAPARTE = contraArr.length ? fmtProv(contraArr[0]) : '';
    g.ABOGADOS_CONTRAPARTE = contraArr.map(fmtProv).filter(Boolean).join('; ');

    // DEMANDADO_LOCALIDAD: viene de Codigos_Postales.Ciudad via CP de contraparte
    g.DEMANDADO_LOCALIDAD = demandado?.localidad || '';

    // ===== RADICACION: Juzgado, Carta Poder, Montos =====
    const radic = raw?.radicacion || {};

    // JUZGADO: Primero de radicación, fallback a auxiliares (grilla 3)
    const juzgadoAux = partes?.auxiliares?.find(p =>
        (p.caracter || '').toLowerCase().includes('juzgad')
    );
    g.JUZGADO = radic?.juzgado || juzgadoAux?.persona || '';
    g.RADICACION = g.JUZGADO;

    // SECRETARIA: Desde auxiliares (grilla 3)
    const secretaria = partes?.auxiliares?.find(p =>
        (p.caracter || '').toLowerCase().includes('secretar')
    );
    g.SECRETARIA = secretaria?.persona || '';

    // CAPITAL_RECLAMADO: Primero de radicación (monto_demanda), luego de expediente
    const montoDemanda = parseFloat(radic?.monto_demanda || 0) || 0;
    const montoExp = parseFloat(raw?.monto_actualizado_demanda || 0) || 0;
    const capitalFinal = montoDemanda || montoExp;
    g.CAPITAL_RECLAMADO = capitalFinal > 0 ? capitalFinal.toLocaleString('es-AR', { minimumFractionDigits: 2 }) : '';
    g.CAPITAL_RECLAMADO_LETRAS = capitalFinal > 0 ? numeroALetras(capitalFinal) : '';
    g.MONTO_ACTUALIZADO = raw?.monto_actualizado_demanda ? parseFloat(raw.monto_actualizado_demanda).toLocaleString('es-AR', { minimumFractionDigits: 2 }) : '';

    // CARTA_PODER: Primero de radicación, luego de partes apoderado
    const apoderado = partes?.provincia?.find(p =>
        (p.caracter || '').toLowerCase().includes('apoderad')
    );
    g.CARTA_PODER = radic?.carta_poder || apoderado?.carta_poder || (raw?.nro_carta_poder ?? '');

    // DOMICILIO_CONSTITUIDO: Desde radicación
    g.DOMICILIO_CONSTITUIDO = radic?.domicilio || '';

    // APODERADO: Desde radicación (incluye matrícula)
    g.APODERADO = radic?.apoderado || apoderado?.persona || '';

    // Datos del movimiento actual
    g.FECHA_MOVIMIENTO = movimientoData?.fecha || '';
    g.DESCRIPCION_MOV = movimientoData?.descripcion || '';
    g.TEXTO_MOV = movimientoData?.texto || movimientoData?.descripcion || '';
    g.TIPO_MOVIMIENTO = movimientoData?.tipo || '';
    g.ESCRITO = movimientoData?.escrito || '';

    // USUARIO: Usuario logueado actual
    g.USUARIO = movimientoData?.usuario || '';

    // NUMERO_RECIBO / RECIBO: Último recibo (se popula si está disponible)
    g.NUMERO_RECIBO = raw?.numero_recibo || '';
    g.RECIBO = g.NUMERO_RECIBO;

    // Compatibilidad con nombres PascalCase/legacy usados en plantillas
    g.Fecha = g.FECHA;
    g.Fecha_Letras = g.FECHA_LETRAS;
    g.Fecha_Mes_Anio = g.FECHA_MES_ANIO;
    g.Dia = g.DIA;
    g.Mes = g.MES;
    g.Anio = g.ANIO;
    g.Autos = g.AUTOS;
    g.Caratula = g.CARATULA;
    g.NroExpediente = g.NRO_EXPEDIENTE;
    g.Tipo_Proceso = g.TIPO_PROCESO;
    g.Actor = g.ACTOR;
    g.Demandado = g.DEMANDADO;
    g.Abogado = g.ABOGADO;
    g.Juzgado = g.JUZGADO;
    g.Secretaria = g.SECRETARIA;
    g.Expediente = g.EXPEDIENTE;
    g.Escrito = g.ESCRITO;
    g.Usuario = g.USUARIO;

    // Alias con guiones bajos (snake_case)
    g.Expediente_Adm = g.EXPEDIENTE_ADM;
    g.Fecha_Mov = g.FECHA_MOVIMIENTO;
    g.Abogado_Domicilio = g.ABOGADO_DOMICILIO;
    g.Demandado_NN = g.DEMANDADO_NN;
    g.Demandado_Direccion = g.DEMANDADO_DIRECCION;
    g.Capital_Reclamado = g.CAPITAL_RECLAMADO;
    g.Capital_Reclamado_Letras = g.CAPITAL_RECLAMADO_LETRAS;
    g.Carta_Poder = g.CARTA_PODER;
    g.Monto_Actualizado = g.MONTO_ACTUALIZADO;
    g.Domicilio_Constituido = g.DOMICILIO_CONSTITUIDO;
    g.Apoderado = g.APODERADO;
    g.Numero_Recibo = g.NUMERO_RECIBO;
    g.Radicacion = g.RADICACION;

    return g;
}
function MovimientosTab({ idExpediente, tipoExpediente, usuario, expedienteInfo, onEmbargoChange }) {
    const classes = useStyles();

    // Data
    const [movimientos, setMovimientos] = useState([]);
    const [tiposMov, setTiposMov] = useState([]);
    const [escritos, setEscritos] = useState([]);

    // Diálogo "Repositorio": movimiento que es solo un link externo (ej. al
    // repositorio del Poder Judicial), se abre en pestaña nueva.
    const [repoDlg, setRepoDlg] = useState({ open: false, descripcion: '', url: '', saving: false });

    // Variables del expediente + override del usuario
    const expedienteVarsRef = useRef({});   // glosario base (del expediente)
    const varsOverrideRef = useRef({});     // lo que complete el usuario en el diálogo
    const missingVarsRef = useRef([]);      // llaves faltantes pendientes de completar
    // Mismo contenido que el ref, pero como estado: el ref no dispara re-render y
    // el aviso de datos faltantes tiene que verse en pantalla, no solo en un snackbar.
    const [missingVars, setMissingVars] = useState([]);

    // Estado de Proceso
    const [estadosDisponibles, setEstadosDisponibles] = useState([]);
    const [estadoActual, setEstadoActual] = useState(null);
    const [openEstado, setOpenEstado] = useState(false);
    const [estadoLoading, setEstadoLoading] = useState(true);

    const normOne = (o) => o ? ({ id: o.id ?? o.Id, nombre: o.nombre ?? o.Nombre ?? o.label ?? o.Etapa ?? o.Estado ?? "" }) : null;

    const loadEtapas = useCallback(async () => {
        setEstadoLoading(true);
        try {
            const [lst, act] = await Promise.all([ETAPAS_API.listar(), ETAPAS_API.actual(idExpediente)]);
            const items = (lst?.estados || []).map(normOne);
            setEstadosDisponibles(items);
            setEstadoActual(normOne(act?.estado));
        } finally {
            setEstadoLoading(false);
        }
    }, [idExpediente]);

    useEffect(() => { loadEtapas(); }, [loadEtapas]);

    // Cache para evitar llamadas repetidas
    const cacheRef = useRef({
        tipos: { data: [], timestamp: 0 },
        escritos: { data: [], timestamp: 0 }
    });
 
    const [escritosCargados, setEscritosCargados] = useState(false);

    // General
    const [loading, setLoading] = useState(true);
    const [highlightedId, setHighlightedId] = useState(null);

    // Filtros avanzados
    const [filtroTipo, setFiltroTipo] = useState("");
    const [filtroFechaDesde, setFiltroFechaDesde] = useState("");
    const [filtroFechaHasta, setFiltroFechaHasta] = useState("");
    const [filtroEstadoProceso, setFiltroEstadoProceso] = useState("");
    const [filtroVinculados, setFiltroVinculados] = useState(false);
    const [filtrosAbiertos, setFiltrosAbiertos] = useState(false);

    // Paginación
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);

    // Export
    const [exportando, setExportando] = useState(false);

    // Permisos por módulo. IMPORTANTE: los expedientes JUDICIALES traen el área
    // en el campo `area`, los ADMINISTRATIVOS en `modulo`. Si solo miramos
    // `.modulo`, en judiciales queda vacío y usePermisosModulo no encuentra los
    // permisos del módulo — solo resuelve los del área global (SECRETARÍA
    // GENERAL Y DAP). Por eso los movimientos solo se habilitaban vía DAP.
    const moduloNombre = expedienteInfo?.modulo || expedienteInfo?.area || '';
    const { permDe, has: hasPerm, ready: permReady, allPerms } = usePermisosModulo(moduloNombre);
    const permMov = permDe("PESTAÑA MOVIMIENTOS", "MOVIMIENTOS");
    const perm = permReady
        ? { visible: true, ...permMov }
        : { visible: false, select: false, insert: false, update: false, delete: false };
    // Reglas E/F para edición/eliminación por ownership
    const [reglaE, setReglaE] = useState(false); // Owner puede editar/eliminar propio
    const [reglaF, setReglaF] = useState(false); // No-owner puede editar/eliminar ajeno
    const isGenCartaPoder = (s) => (s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").trim().toUpperCase() === "GENERADOR DE CARTA PODER";

    // Carta Poder: buscar permiso en el módulo actual
    const puedeGenerarCP = React.useMemo(() =>
        hasPerm("CARTA PODER", "SELECT") || hasPerm("CARTA PODER", "REPORT"),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [permReady, moduloNombre]);

    // Regla E/F: verificar si el usuario puede editar/eliminar un movimiento específico
    const puedeModificarMov = useCallback((mov) => {
        if (!mov) return false;
        const esOwner = (mov.responsable || "").toLowerCase() === (usuario || "").toLowerCase();
        // Owner + Regla E, o No-owner + Regla F
        return (esOwner && reglaE) || (!esOwner && reglaF);
    }, [usuario, reglaE, reglaF]);

    // Edit/crear
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editData, setEditData] = useState({});
    const editDataRef = useRef({});
    const [isNew, setIsNew] = useState(false);
    const [saving, setSaving] = useState(false);
    const [embargoValidado, setEmbargoValidado] = useState(false);
    const [escritoContenido, setEscritoContenido] = useState("");
    // Template del escrito (HTML con las variables {X} aún sin reemplazar).
    // Sirve para regenerar el contenido en vivo cuando el usuario tipea descripción
    // o fojas — se aplica replaceVarsInHtml sobre este template, no sobre el HTML ya
    // resuelto, así no perdemos las variables al actualizarlas.
    const escritoTemplateRef = useRef('');
    // Historial de descripciones (localStorage, ver autocomplete del campo).
    // Se recarga al abrir el dialog para reflejar lo que se guardó en otros movs.
    const [descripcionesHistorial, setDescripcionesHistorial] = useState(loadDescripcionesHistorial);

    // Mantener refs sincronizadas con el estado
    useEffect(() => {
        escritoContenidoRef.current = escritoContenido;
    }, [escritoContenido]);

    useEffect(() => {
        editDataRef.current = editData;
    }, [editData]);

    const escritosFiltrados = useMemo(() => {
        // Tipo de expediente numérico: 1=Judicial, 2=Administrativo
        const teNum = tipoExpediente === "judicial" ? 1 : 2;

        // ⭐ FILTRO GENERAL: Solo escritos activos + filtrar por tipo de expediente
        const activos = (escritos || []).filter(e => {
            const activo = e?.activo ?? e?.Activo;
            if (!(activo === true || activo === 1 || activo === '1')) return false;

            // Filtrar por tipo de expediente (si el escrito tiene categoría con tipo_expediente)
            const te = e?.tipo_expediente ?? e?.Tipo_Expediente;
            if (te != null && Number(te) !== 0 && Number(te) !== teNum) return false;

            // Excluir privados del pool general (solo van en MIS ESCRITOS)
            const scope = (e?.scope || e?.Scope || "").toUpperCase().trim();
            const esPrivado = scope === "PRIVATE" || scope === "PRIVADO";
            if (esPrivado) return false;

            return true;
        });

        if (!editData.tipo) return activos;

        if (isTipo(editData.tipo, "MIS ESCRITOS")) {
            // MIS ESCRITOS: solo privados del usuario actual (sin filtro de tipo_expediente)
            const resultado = (escritos || []).filter(e => {
                const estado = (e?.estado || e?.Estado || "").toUpperCase().trim();
                const scope = (e?.scope || e?.Scope || "").toUpperCase().trim();
                const owner = (e?.ownerUsuario || e?.OwnerUsuario || e?.owner_usuario || "").trim();

                const esPublicado = estado === "PUBLICADO";
                const esPrivado = scope === "PRIVATE" || scope === "PRIVADO";
                const esMio = owner === usuario;

                return esPublicado && esPrivado && esMio;
            });

            return resultado;
        }

        // ⭐ Para Carta Poder, filtrar escritos que contengan "carta poder"
        if (isGenCartaPoder(editData.tipo)) {
            const cpRe = /carta[\s_-]*poder/i;
            return activos.filter(e =>
                cpRe.test(e?.escrito || "") || cpRe.test(e?.descripcion || "")
            );
        }

        // ⭐ Para tipos procesales, filtrar según categoría
        if (isTipo(editData.tipo, "PROCESAL")) {
            const cpRe = /carta[\s_-]*poder/i;
            return activos.filter(e => {
                const cat = (e?.categoria || "").toLowerCase();
                const nombre = (e?.escrito || e?.Escrito || "");

                // Excluir: Recibos, Cédulas, Carta Poder, Readjudicación
                const esReciboCedula = cat.includes("recibo") || cat.includes("cedula") || cat.includes("cédula");
                const esCartaPoder = cpRe.test(nombre) || cpRe.test(e?.descripcion || "");
                const esReadjudicacion = cat.includes("readjudicaci");

                return !esReciboCedula && !esCartaPoder && !esReadjudicacion;
            });
        }

        // ⭐ Para Extra Procesal (jud): solo categoría "Extra procesal"
        if (isTipo(editData.tipo, "EXTRA PROCESAL")) {
            return activos.filter(e => {
                const cat = (e?.categoria || "").toLowerCase();
                return cat.includes("extra procesal");
            });
        }

        // ⭐ Para Recibos (admin): solo categoría "Recibo administrativo"
        if (isTipo(editData.tipo, "RECIBOS")) {
            return activos.filter(e => {
                const cat = (e?.categoria || "").toLowerCase();
                return cat.includes("recibo administrativo");
            });
        }

        // Default: excluir readjudicación
        return activos.filter(e => {
            const cat = (e?.categoria || "").toLowerCase();
            return !cat.includes("readjudicaci");
        });
    }, [escritos, editData.tipo, usuario, tipoExpediente]);
  
    // Estados para el editor externo
    const [editorKey, setEditorKey] = useState(0); // Para forzar re-render del editor
    const editorApiRef = useRef(null); // Para guardar las funciones del editor
    const escritoContenidoRef = useRef(""); // ⭐ Para tener siempre el valor actual

    // #2 — Redacción libre del escrito (sin partir de una plantilla) + opción de
    // guardar lo redactado como documento reutilizable (escrito privado del usuario).
    const [redactarEnBlanco, setRedactarEnBlanco] = useState(false);
    const [guardarDocDialog, setGuardarDocDialog] = useState({ open: false, nombre: '', saving: false });

    // Callback cuando el editor está listo
    const handleEditorReady = useCallback((api) => {
        const contenidoPendiente = escritoContenidoRef.current;

        editorApiRef.current = api;

        // ⭐ Cargar contenido que pudo haber llegado antes que el editor
        if (contenidoPendiente) {

            // Pequeño delay para asegurar que TinyMCE está completamente inicializado
            setTimeout(() => {
                if (api?.setContent) {
                    api.setContent(contenidoPendiente);
                }
            }, 150);
        }
    }, []); // ⭐ Sin dependencias, usa el ref que siempre está actualizado

    // Callback cuando cambia el contenido
    const handleEditorChange = useCallback((html) => {
        setEscritoContenido(html);
    }, []);

    useEffect(() => {

        if (!editorApiRef.current?.setContent) {

            return;
        }

        if (escritoContenido !== undefined) {
            const currentContent = editorApiRef.current.getContent() || '';
            const newContent = escritoContenido || '';

            // Solo actualizar si el contenido es diferente
            if (String(currentContent).trim() !== String(newContent).trim()) {

                editorApiRef.current.setContent(newContent);
            } else {

            }
        }
    }, [escritoContenido, editorKey]);

    // Actualización en vivo del editor cuando el usuario tipea descripción o fojas.
    // Reemplaza variables del template guardado (escritoTemplateRef) con el state
    // actual del dialog y refresca el editor — sólo si éste NO tiene foco
    // (no interrumpir si el usuario está editando manualmente el escrito).
    useEffect(() => {
        if (!dialogOpen) return;
        if (!escritoTemplateRef.current) return;
        const t = setTimeout(() => {
            const editor = editorApiRef.current;
            if (!editor || typeof editor.setContent !== 'function') return;
            try { if (editor.hasFocus && editor.hasFocus()) return; } catch { /* ignore */ }

            const movVars = {
                DESCRIPCION_MOV: editData.descripcion || '',
                Descripcion_Mov: editData.descripcion || '',
                FOJAS: editData.fojas || '',
                CANTIDAD_FOJAS: editData.fojas || '',
                Cantidad_Fojas: editData.fojas || '',
                NUMERO_RECIBO: editData.fojas || '',
                Numero_Recibo: editData.fojas || '',
                RECIBO: editData.fojas || '',
                FECHA_MOVIMIENTO: editData.fecha || '',
                Fecha_Mov: editData.fecha || '',
            };
            const allVars = { ...expedienteVarsRef.current, ...movVars, ...varsOverrideRef.current };
            const { html } = replaceVarsInHtml(escritoTemplateRef.current, allVars);
            const current = editor.getContent() || '';
            if (String(current).trim() !== String(html || '').trim()) {
                editor.setContent(html || '');
            }
        }, 400);
        return () => clearTimeout(t);
    }, [editData.descripcion, editData.fojas, editData.fecha, dialogOpen]);

    // Adjunto PDF
    const [adjuntoNombre, setAdjuntoNombre] = useState("");
    const [adjuntoBase64, setAdjuntoBase64] = useState("");

    // Preview escrito
    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewHtml, setPreviewHtml] = useState("");
    const [previewTitle, setPreviewTitle] = useState("");
    const [previewPdfUrl, setPreviewPdfUrl] = useState("");
    const [previewVinculado, setPreviewVinculado] = useState(null); // {id, ext} cuando es vinculado
    const [descargandoPdf, setDescargandoPdf] = useState(false);

    // Delete
    const [dialogDeleteOpen, setDialogDeleteOpen] = useState(false);
    const [deleteId, setDeleteId] = useState(null);
    const [deleteInfo, setDeleteInfo] = useState(null);

    // Snackbar
    const [snackbar, setSnackbar] = useState({ open: false, msg: "", severity: "success" });

    // Dialog "vinculado eliminado"
    const [vinculadoEliminadoOpen, setVinculadoEliminadoOpen] = useState(false);



     // === Confirm dialog sin dependencias reactivas ===
    const confirmResolveRef = useRef(null);
    const [confirmDlg, setConfirmDlg] = useState({
        open: false, title: 'Confirmar', content: '',
        confirmText: 'Aceptar', cancelText: 'Cancelar'
    });


    const handleConfirmClose = useCallback((answer) => {
        const r = confirmResolveRef.current;
        confirmResolveRef.current = null;
        setConfirmDlg(prev => ({ ...prev, open: false }));
        if (r) r(!!answer);
    }, []);

    const [viewMode, setViewMode] = useState("table"); // "table" | "timeline" | "calendar" | "stats"
    const [calendarEvent, setCalendarEvent] = useState(null); // movimiento seleccionado en calendario
    const [statsFilter, setStatsFilter] = useState(null); // filtro activo en vista stats: null | "total" | "adjunto" | "estado" | {tipo: "..."} | {mes: "..."}
    const [fechaFuturaConfirmada, setFechaFuturaConfirmada] = useState(false);
    const [duplicadoConfirmado, setDuplicadoConfirmado] = useState(false);

    // Resetear flags de confirmación al cerrar el diálogo
    useEffect(() => {
        if (!dialogOpen) {
            setFechaFuturaConfirmada(false);
            setDuplicadoConfirmado(false);
        }
    }, [dialogOpen]);

    // Orden/búsqueda
    const [orderBy, setOrderBy] = useState("fecha");
    const [order, setOrder] = useState("desc");
    const [q, setQ] = useState("");

    const isCartaPoder = (s) => (s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").trim().toUpperCase() === "CARTA PODER";

    const [descAutoFromCP, setDescAutoFromCP] = useState(false);
    const descAutoValueRef = useRef("");

    useEffect(() => {
        let mounted = true;

        // Permisos ahora se cargan via usePermisosModulo hook

        async function fetchDatos() {
            setLoading(true);
            try {
                const now = Date.now();

                // ⭐ Usar cache para tipos si está vigente
                let tipos = [];
                if (now - cacheRef.current.tipos.timestamp < CACHE_TTL && cacheRef.current.tipos.data.length > 0) {
                    tipos = cacheRef.current.tipos.data;
                } else {
                    const tiposRes = await getCombosJWT('tipo_movimientos', '', 200);
                    tipos = normalizeKeys(tiposRes?.items || tiposRes?.tipos || []);
                    cacheRef.current.tipos = { data: tipos, timestamp: now };
                }

                // ⭐ Cargar solo movimientos (escritos se cargan lazy)
                const movsRes = await expedienteMovimientosJWT(idExpediente);
                const movs = { movimientos: normalizeKeys(movsRes?.movimientos || []) };

                // Reglas E/F de permisos por ownership
                if (movsRes?.regla_e !== undefined) setReglaE(!!movsRes.regla_e);
                if (movsRes?.regla_f !== undefined) setReglaF(!!movsRes.regla_f);

                if (!mounted) return;

                setMovimientos((movs?.movimientos || []).map(m => {
                    const raw = m.fecha;
                    let sort = 0;

                    const mm = String(raw || "").match(
                        /^(\d{4})-(\d{2}-\d{2})\s+(\d{2}):(\d{2})(?::(\d{2}))?$/
                    );
                    if (mm) {
                        const y = +mm[1], mo = +mm[2] - 1, d = +mm[3];
                        const h = +mm[4], mi = +mm[5], s = +(mm[6] || 0);
                        sort = new Date(y, mo, d, h, mi, s).getTime();
                    } else {
                        const d = parseFechaAny(raw);
                        sort = d ? d.getTime() : 0;
                    }

                    return { ...m, fechaRaw: raw, fechaSort: sort };
                }));
                setTiposMov(tipos);

            } catch {
                setSnackbar({ open: true, msg: "Error cargando movimientos.", severity: "error" });
            } finally {
                if (mounted) setLoading(false);
            }
        }

        async function fetchEstados() {
            setEstadoLoading(true);
            try {
                const [act, lista] = await Promise.all([
                    fetchEstadoActual(idExpediente),
                    fetchEstadosDisponibles(tipoExpediente)
                ]);
                if (!mounted) return;
                if (act) setEstadoActual(act);
                setEstadosDisponibles((lista || []).filter(e => (e && String(e.nombre || e.label || "").trim() !== "")));
            } finally {
                setEstadoLoading(false);
            }
        }

        (async () => {
            await fetchDatos();
            await fetchEstados();
        })();

        return () => { mounted = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [idExpediente, usuario, tipoExpediente]);

    // ⭐ 7A) Lazy loading de escritos - Solo cargar cuando se necesitan
    const cargarEscritos = useCallback(async () => {
        if (escritosCargados) return; // Ya están cargados

        const now = Date.now();

        // Verificar cache
        if (now - cacheRef.current.escritos.timestamp < CACHE_TTL && cacheRef.current.escritos.data.length > 0) {
            setEscritos(cacheRef.current.escritos.data);
            setEscritosCargados(true);
            return;
        }

        try {
            const res = await escritosComboJWT();
            const items = res?.items || [];
            setEscritos(items);
            cacheRef.current.escritos = { data: items, timestamp: Date.now() };
            setEscritosCargados(true);
        } catch (error) {
            console.error('[ESCRITOS] Error cargando escritos:', error);
            setEscritos([]);
            setEscritosCargados(true);
            setSnackbar({ open: true, msg: "Error cargando escritos.", severity: "error" });
        }
    }, [escritosCargados]);

    // #2 — Guarda el contenido actual del editor como un documento reutilizable
    // (escrito privado del usuario, publicado para que aparezca en "Mis Escritos").
    const guardarComoDocumento = async () => {
        const nombre = String(guardarDocDialog.nombre || '').trim();
        if (!nombre) {
            setSnackbar({ open: true, msg: "Ponele un nombre al documento.", severity: "warning" });
            return;
        }
        const contenido = editorApiRef.current?.getContent?.() ?? escritoContenidoRef.current ?? '';
        if (!String(contenido).trim()) {
            setSnackbar({ open: true, msg: "El escrito está vacío, no hay nada para guardar.", severity: "warning" });
            return;
        }
        setGuardarDocDialog(d => ({ ...d, saving: true }));
        try {
            const res = await documentosSaveJWT({
                titulo: nombre,
                escrito: nombre,
                descripcion: '',
                contenido,
                scope: 'PRIVATE',
                estado: 'PUBLICADO',
                activo: true,
            });
            if (res?.ok) {
                setGuardarDocDialog({ open: false, nombre: '', saving: false });
                setSnackbar({ open: true, msg: "Documento guardado en Mis Escritos.", severity: "success" });
                // Refrescar el combo para que el documento nuevo sea seleccionable ya.
                try {
                    const combo = await escritosComboJWT();
                    const items = combo?.items || [];
                    setEscritos(items);
                    cacheRef.current.escritos = { data: items, timestamp: Date.now() };
                } catch { /* refresco no-bloqueante */ }
            } else {
                setGuardarDocDialog(d => ({ ...d, saving: false }));
                setSnackbar({ open: true, msg: res?.mensaje || "No se pudo guardar el documento.", severity: "error" });
            }
        } catch (err) {
            console.error('[GUARDAR DOC] Error:', err);
            setGuardarDocDialog(d => ({ ...d, saving: false }));
            setSnackbar({ open: true, msg: "Error de red al guardar el documento.", severity: "error" });
        }
    };

    const recargarMovimientos = useCallback(async () => {
        try {
            const movsRes = await expedienteMovimientosJWT(idExpediente);
            const movsList = normalizeKeys(movsRes?.movimientos || []);
            // Actualizar reglas E/F si vienen
            if (movsRes?.regla_e !== undefined) setReglaE(!!movsRes.regla_e);
            if (movsRes?.regla_f !== undefined) setReglaF(!!movsRes.regla_f);

            setMovimientos(movsList.map(m => {
                const raw = m.fecha || m.fecha_movimiento;
                let sort = 0;

                const mm = String(raw || "").match(
                    /^(\d{4})-(\d{2})-(\d{2})\s+(\d{2}):(\d{2})(?::(\d{2}))?$/
                );
                if (mm) {
                    const y = +mm[1], mo = +mm[2] - 1, d = +mm[3];
                    const h = +mm[4], mi = +mm[5], s = +(mm[6] || 0);
                    sort = new Date(y, mo, d, h, mi, s).getTime();
                } else {
                    const d = parseFechaAny(raw);
                    sort = d ? d.getTime() : 0;
                }

                return { ...m, fechaRaw: raw, fechaSort: sort };
            }));
        } catch (error) {
            console.error("Error recargando movimientos:", error);
            setSnackbar({ open: true, msg: "Error al recargar movimientos.", severity: "error" });
        }
    }, [idExpediente]); // ⭐ Agregar dependencia

    // Crear un movimiento "Repositorio" (solo un link externo al repositorio del PJ).
    const guardarRepositorio = async () => {
        const url = (repoDlg.url || "").trim();
        if (!/^https?:\/\//i.test(url)) {
            setSnackbar({ open: true, severity: "warning", msg: "Ingresá una URL válida (debe empezar con http:// o https://)" });
            return;
        }
        setRepoDlg(p => ({ ...p, saving: true }));
        try {
            const now = new Date();
            const pad = n => String(n).padStart(2, "0");
            const fecha = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())} ${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`;
            const res = await crearMovimientoJWT(idExpediente, {
                idExpediente,
                tipo: "REPOSITORIO",
                descripcion: (repoDlg.descripcion || "").trim() || "Repositorio",
                fecha,
                usuario,
                urlVinculado: url,
            });
            if (res?.ok) {
                setRepoDlg({ open: false, descripcion: "", url: "", saving: false });
                await recargarMovimientos();
                setSnackbar({ open: true, severity: "success", msg: "Movimiento Repositorio creado." });
            } else {
                setSnackbar({ open: true, severity: "error", msg: res?.mensaje || "Error al crear el movimiento." });
                setRepoDlg(p => ({ ...p, saving: false }));
            }
        } catch {
            setSnackbar({ open: true, severity: "error", msg: "Error al crear el movimiento." });
            setRepoDlg(p => ({ ...p, saving: false }));
        }
    };

    // ⭐ 7C) Auto-refresh cada 3 minutos
    useEffect(() => {
        const intervalo = setInterval(() => {
            // Solo refrescar si no hay diálogo abierto
            if (!dialogOpen && !dialogDeleteOpen && !previewOpen) {
                recargarMovimientos();
            }
        }, 3 * 60 * 1000); // 3 minutos

        return () => clearInterval(intervalo);
    }, [dialogOpen, dialogDeleteOpen, previewOpen, recargarMovimientos]);

    const movimientosFiltrados = useMemo(() => {
        let resultado = movimientos;

        // Filtro por tipo
        if (filtroTipo) {
            resultado = resultado.filter(m => keyify(m.tipo) === keyify(filtroTipo));
        }

        // ⭐ NUEVO: Filtro por Estado Proceso
        if (filtroEstadoProceso) {
            resultado = resultado.filter(m => {
                if (!isTipo(m.tipo, "ESTADO PROCESO")) return false;
                const desc = plainText(m.descripcion || "");
                return desc.includes(filtroEstadoProceso);
            });
        }

        // Filtro por vinculados (movimientos con adjunto)
        if (filtroVinculados) {
            resultado = resultado.filter(m =>
                (m.adjunto_url && String(m.adjunto_url).trim()) ||
                (m.escrito_contenido && String(m.escrito_contenido).trim()) ||
                hasEscrito(m.escrito)
            );
        }

        // Filtro por rango de fechas
        if (filtroFechaDesde) {
            const desde = parseFechaAny(filtroFechaDesde);
            if (desde) {
                desde.setHours(0, 0, 0, 0);
                resultado = resultado.filter(m => {
                    const fechaMov = parseFechaAny(m.fechaRaw || m.fecha);
                    return fechaMov && fechaMov >= desde;
                });
            }
        }

        if (filtroFechaHasta) {
            const hasta = parseFechaAny(filtroFechaHasta);
            if (hasta) {
                hasta.setHours(23, 59, 59, 999);
                resultado = resultado.filter(m => {
                    const fechaMov = parseFechaAny(m.fechaRaw || m.fecha);
                    return fechaMov && fechaMov <= hasta;
                });
            }
        }

        // Búsqueda por texto
        if (q) {
            const nq = norm(q);
            resultado = resultado.filter(m => {
                const desc = norm(plainText(m.descripcion));
                const esc = norm(plainText(m.escrito_contenido));
                const tipo = norm(displayTipoLabel(m.tipo));
                const resp = norm(m.responsable);
                const fec = norm(formatFechaHora(m.fechaRaw || m.fecha));
                return desc.includes(nq) || esc.includes(nq) || tipo.includes(nq) || resp.includes(nq) || fec.includes(nq);
            });
        }

        return resultado;
    }, [movimientos, q, filtroTipo, filtroEstadoProceso, filtroVinculados, filtroFechaDesde, filtroFechaHasta]);

    // Resetear página al cambiar filtros
    useEffect(() => {
        setPage(0);
    }, [q, filtroTipo, filtroEstadoProceso, filtroVinculados, filtroFechaDesde, filtroFechaHasta]); 

    const handleSort = (field) => {
        if (orderBy === field) setOrder(order === "asc" ? "desc" : "asc");
        else { setOrderBy(field); setOrder(field === "fecha" ? "desc" : "asc"); }
    };

    const movimientosOrdenados = useMemo(() => {
        return movimientosFiltrados.slice().sort((a, b) => {
            // Ordenamiento primario
            let comparison = 0;

            if (orderBy === "fecha") {
                const da = a.fechaSort ?? 0, db = b.fechaSort ?? 0;
                comparison = da - db;

                // Si las fechas son iguales, ordenar por tipo como secundario
                if (comparison === 0) {
                    const tipoA = displayTipoLabel(a.tipo).toLowerCase();
                    const tipoB = displayTipoLabel(b.tipo).toLowerCase();
                    comparison = tipoA.localeCompare(tipoB, "es", { sensitivity: "base" });
                }
            } else if (orderBy === "descripcion") {
                comparison = plainText(a.descripcion).localeCompare(plainText(b.descripcion), "es", { sensitivity: "base" });
            } else {
                comparison = (a[orderBy] || "").toString().localeCompare((b[orderBy] || "").toString(), "es", { sensitivity: "base" });
            }

            // Si aún empatan y no estamos ordenando por fecha, usar fecha como terciario
            if (comparison === 0 && orderBy !== "fecha") {
                const da = a.fechaSort ?? 0, db = b.fechaSort ?? 0;
                comparison = db - da; // Siempre más recientes primero como terciario
            }

            return order === "asc" ? comparison : -comparison;
        });
    }, [movimientosFiltrados, orderBy, order]);

    // Aplicar paginación
    const movimientosPaginados = useMemo(() => {
        const inicio = page * rowsPerPage;
        return movimientosOrdenados.slice(inicio, inicio + rowsPerPage);
    }, [movimientosOrdenados, page, rowsPerPage]);

    // Convertir movimientos a eventos de calendario
    const eventosCalendario = useMemo(() => {
        return movimientosOrdenados.map(m => {
            const fecha = parseFechaAny(m.fechaRaw || m.fecha);
            return {
                id: m.id,
                title: `${displayTipoLabel(m.tipo)} - ${plainText(m.descripcion || "").substring(0, 50)}`,
                start: fecha,
                end: fecha,
                resource: m
            };
        });
    }, [movimientosOrdenados]);

    // Exportar movimientos a PDF con formato
    const exportarPDF = useCallback(() => {
        setExportando(true);

        try {
            // Detectar tipo igual que en HistorialCambios
            let tipoExp = expedienteInfo?.tipo === "judicial" ? "Judicial" : "Administrativo";
            let campos = [];

            if (tipoExp === "Judicial") {
                campos = [
                    { label: "Autos:", value: expedienteInfo?.autos_1_instancia || expedienteInfo?.nro_expediente || "-" },
                    { label: "Carátula:", value: expedienteInfo?.caratula || "-" },
                    { label: "Tipo Proceso:", value: expedienteInfo?.tipo_proceso || "-" }
                ];
            } else {
                campos = [
                    { label: "Nro Expediente:", value: expedienteInfo?.nro_expediente || "-" },
                    { label: "Iniciador:", value: expedienteInfo?.iniciador || "-" },
                    { label: "Extracto:", value: expedienteInfo?.extracto || "-" }
                ];
            }

            const estado = expedienteInfo?.estado || expedienteInfo?.estado_proceso || (estadoActual?.nombre ? optLabel(estadoActual) : "-");

            getBase64FromImage(logoSIGIF, (logoBase64) => {
                const doc = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4" });

                // ---- Título, subtítulo y logo ----
                doc.setFontSize(14);
                doc.setFont("helvetica", "bold");
                doc.setTextColor(23, 30, 60);
                doc.text("Fiscalía de Estado - San Juan", 40, 60);

                doc.setFontSize(12);
                doc.setFont("helvetica", "normal");
                doc.setTextColor(31, 75, 130);
                doc.text(`Expediente ${tipoExp} - Movimientos`, 40, 80);

                // Logo arriba derecha
                doc.addImage(
                    logoBase64,
                    "PNG",
                    doc.internal.pageSize.getWidth() - 175,
                    28,
                    150,
                    65
                );

                // ---- Campos del expediente ----
                let startY = 110;
                const lineSpacing = 20;

                doc.setFontSize(12);
                doc.setFont("helvetica", "bold");
                doc.setTextColor(23, 105, 170);

                campos.forEach((campo, idx) => {
                    doc.text(campo.label, 40, startY + idx * lineSpacing);
                });

                doc.setFont("helvetica", "normal");
                doc.setTextColor(24, 52, 96);
                campos.forEach((campo, idx) => {
                    const texto = String(campo.value).replace(/\s+/g, ' ').trim();
                    doc.text(texto, 170, startY + idx * lineSpacing);
                });

                // ---- Estado destacado ----
                doc.text("Estado:", 40, startY + campos.length * lineSpacing);
                doc.setFillColor(23, 105, 170);
                doc.roundedRect(170, startY + campos.length * lineSpacing - 13, 330, 27, 10, 10, "F");
                doc.setFont("helvetica", "bold");
                doc.setFontSize(12);
                doc.setTextColor(255, 255, 255);
                doc.text(estado, 185, startY + campos.length * lineSpacing + 6);

                // ---- Tabla de movimientos ----
                doc.setFontSize(12);
                doc.setTextColor(0);

                autoTable(doc, {
                    startY: startY + campos.length * lineSpacing + 40,
                    head: [["Fecha", "Tipo", "Descripción", "Responsable"]],
                    body: movimientosOrdenados.map(m => {
                        const desc = limpiarDetalleParaPDF(plainText(m.descripcion || ""));
                        return [
                            formatFechaHora(m.fechaRaw || m.fecha),
                            displayTipoLabel(m.tipo),
                            desc.substring(0, 100),
                            m.responsable || "-"
                        ];
                    }),
                    styles: {
                        font: "helvetica",
                        fontSize: 9,
                        cellPadding: 2,
                        overflow: 'linebreak'
                    },
                    columnStyles: {
                        0: { cellWidth: 75 },
                        1: { cellWidth: 65 },
                        2: { cellWidth: 330 },
                        3: { cellWidth: 70 }
                    },
                    headStyles: {
                        fillColor: [23, 105, 170],
                        textColor: 255,
                        fontSize: 10
                    },
                    bodyStyles: { textColor: 30 },
                    margin: { left: 30, right: 30 }
                });

                // ---- Footer ----
                const pageCount = doc.internal.getNumberOfPages();
                for (let i = 1; i <= pageCount; i++) {
                    doc.setPage(i);
                    doc.setFontSize(8);
                    doc.setTextColor(150);
                    doc.text(
                        `Generado el ${new Date().toLocaleDateString('es-AR')} - Página ${i} de ${pageCount}`,
                        40,
                        doc.internal.pageSize.getHeight() - 20
                    );
                }

                const pdfUrl = doc.output('bloburl');
                window.open(pdfUrl, '_blank');

                setSnackbar({
                    open: true,
                    msg: `PDF generado con ${movimientosOrdenados.length} movimientos.`,
                    severity: "success"
                });

                setExportando(false);
            });

        } catch (error) {
            console.error("Error generando PDF:", error);
            setSnackbar({ open: true, msg: "Error al generar PDF.", severity: "error" });
            setExportando(false);
        }
    }, [movimientosOrdenados, expedienteInfo, estadoActual]);

    const tiposParaMenu = useMemo(() => {
        const pagos = Object.keys(allPerms || {}).some(k => normKey(k).includes("PAGO"));

        // ⭐ TIPOS QUE NUNCA SE MUESTRAN EN EL MENÚ
        const SIEMPRE_OCULTOS = [
            "ADJUNTO BASE-64",
            "EXPEDIENTE ELECTRONICO",
            "FORMULARIOS",
            "REGISTRO CANCELADO",
            "REQUERIMIENTOS",
            "VINCULADOS",
            "ESTADO PROCESO",
            "GENERADOR DE CARTA PODER" // Lo manejamos con modal
        ].map(keyify);

        // Filtrar tipos disponibles de la BD
        const tiposDisponibles = (tiposMov || [])
            .filter(t => {
                const k = keyify(t.tipo);
                if (SIEMPRE_OCULTOS.includes(k)) return false;
                if (k === keyify("CARTA PODER") && !puedeGenerarCP) return false;
                return true;
            })
            .map(t => ({ ...t, labelUI: displayTipoLabel(t.tipo), tipoKey: keyify(t.tipo) }));

        const tiposMap = new Map(tiposDisponibles.map(t => [t.tipoKey, t]));

        // ⭐ ORDEN PERSONALIZADO
        let ordenado = [];

        if (tipoExpediente === "judicial") {
            // Grupo 1: Documentos principales
            const grupo1Keys = ["CARTA PODER", "CEDULA", "REFERENCIA"];
            grupo1Keys.forEach(k => {
                const tipo = tiposMap.get(keyify(k));
                if (tipo) ordenado.push(tipo);
            });

            // Separador
            if (ordenado.length > 0) ordenado.push({ tipo: "__divider_1", labelUI: "", isDivider: true });

            // Grupo 2: Procesales
            const grupo2Keys = ["PROCESAL", "EXTRA PROCESAL"];
            grupo2Keys.forEach(k => {
                const tipo = tiposMap.get(keyify(k));
                if (tipo) ordenado.push(tipo);
            });

            // Separador
            if (ordenado.some(t => grupo2Keys.includes(t.tipoKey))) {
                ordenado.push({ tipo: "__divider_2", labelUI: "", isDivider: true });
            }

        } else {
            // Administrativo
            // Grupo 1: Principales
            const grupo1Keys = ["OTRO MOV", "REFERENCIA"];
            grupo1Keys.forEach(k => {
                const tipo = tiposMap.get(keyify(k));
                if (tipo) ordenado.push(tipo);
            });

            // Pagos
            if (pagos) {
                ["OFIGEN", "OFICINA DE PAGOS"].forEach(k => {
                    const tipo = tiposMap.get(keyify(k));
                    if (tipo) ordenado.push(tipo);
                });
            }

            // Separador
            if (ordenado.length > 0) ordenado.push({ tipo: "__divider_1", labelUI: "", isDivider: true });

            // ⭐ NUEVO: Recibos (para administrativos)
            ordenado.push({
                tipo: "RECIBOS",
                labelUI: "Recibos",
                tipoKey: keyify("RECIBOS"),
                __virtual: true
            });

            // Separador
            ordenado.push({ tipo: "__divider_2", labelUI: "", isDivider: true });
        }

        // ⭐ Embargo (para todos)
        const embargoTipo = tiposMap.get(keyify("EMBARGO"));
        if (embargoTipo) {
            ordenado.push(embargoTipo);
            ordenado.push({ tipo: "__divider_embargo", labelUI: "", isDivider: true });
        }

        // ⭐ Mis Escritos (para todos)
        ordenado.push({
            tipo: "MIS ESCRITOS",
            labelUI: "Mis Escritos",
            tipoKey: keyify("MIS ESCRITOS"),
            __virtual: true
        });

        return ordenado;
    }, [tiposMov, tipoExpediente, allPerms, puedeGenerarCP]);

    const tipoActual = editData?.tipo || "";
    const opcionesTipo = useMemo(() => {
        let opts = tiposParaMenu;
        if (tipoActual && !opts.some(t => keyify(t.tipo) === keyify(tipoActual))) {
            opts = [...opts, { tipo: tipoActual, labelUI: displayTipoLabel(tipoActual), __extra: true }];
        }
        return opts;
    }, [tiposParaMenu, tipoActual]);

    const todayLocalISO = () => {
        const d = new Date();
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, "0");
        const day = String(d.getDate()).padStart(2, "0");
        return `${y}-${m}-${day}`;
    };

    const startAdd = async () => {  // ⭐ Hacer async
        if (!perm.insert) {
            setSnackbar({ open: true, msg: "No tenés permiso para crear movimientos.", severity: "warning" });
            return;
        }

        // ⭐ Cargar escritos antes de abrir el diálogo
        await cargarEscritos();

        setEditorKey(prev => prev + 1);

        setEditData({
            fecha: todayLocalISO(),
            tipo: "",
            descripcion: "",
            escrito: "",
            // Embargo fields (reset)
            embargoEstado: "",
            embargoFecha: "",
            embargoMonto: null,
            embargoFolioDepositoJudicial: "",
            embargoBancoEmbargante: "",
            embargoNumTransaccion: "",
            embargoChequeJudicial: "",
        });
        setEscritoContenido("");
        setRedactarEnBlanco(false);
        setAdjuntoNombre("");
        setAdjuntoBase64("");
        setEmbargoValidado(false);
        setIsNew(true);
        missingVarsRef.current = [];
        setMissingVars([]);
        setDialogOpen(true);
    };

    const startEdit = async (id) => {
        if (!perm.update) {
            setSnackbar({ open: true, msg: "No tenés permiso para editar.", severity: "warning" });
            return;
        }

        // ⭐ Cargar escritos antes de abrir el diálogo
        await cargarEscritos();

        const mov = movimientos.find(m => m.id === id);

        // No permitir editar Estado Proceso
        if (isTipo(mov?.tipo, "ESTADO PROCESO")) {
            setSnackbar({
                open: true,
                msg: "Los movimientos de tipo 'Estado Procesal' no son editables.",
                severity: "warning"
            });
            return;
        }

        const raw = String(mov?.fechaRaw || mov?.fecha || "");
        const m = raw.match(/^(\d{4}-\d{2}-\d{2})/);
        const soloFecha = m ? m[1] : todayLocalISO();

        // Separar fojas de la descripción si es expediente administrativo
        let descSinFojas = mov?.descripcion || "";
        let fojasVal = "";
        if (tipoExpediente !== "judicial") {
            const fojasMatch = String(descSinFojas).match(/^(.*?)\s*\(fs\.\s*(.+?)\)\s*$/);
            if (fojasMatch) {
                descSinFojas = fojasMatch[1];
                fojasVal = fojasMatch[2];
            }
        }

        // Si es EMBARGO, cargar datos del embargo vinculado
        let embargoData = {};
        if (isEmbargo(mov?.tipo)) {
            try {
                const embRes = await embargoByMovimientoJWT(idExpediente, id);
                if (embRes?.ok && embRes.embargo) {
                    const e = embRes.embargo;
                    embargoData = {
                        embargoEstado: e.estado || "",
                        embargoFecha: e.fecha || "",
                        embargoMonto: e.monto != null ? e.monto : null,
                        embargoFolioDepositoJudicial: e.folio_deposito_judicial || "",
                        embargoBancoEmbargante: e.banco_embargante || "",
                        embargoNumTransaccion: e.num_transaccion || "",
                        embargoChequeJudicial: e.cheque_judicial || "",
                    };
                }
            } catch (err) {
                console.error('[EMBARGO] Error cargando datos:', err);
            }
        }

        setEditData({ ...mov, fecha: soloFecha, descripcion: descSinFojas, fojas: fojasVal, ...embargoData });
        setIsNew(false);
        setAdjuntoNombre("");
        setAdjuntoBase64("");

        // ⭐ CORRECCIÓN: Usar escritoContenido guardado si existe
        if (mov.escrito_contenido && String(mov.escrito_contenido).trim()) {
            // Ya tiene un escrito guardado, usarlo directamente
            setEscritoContenido(mov.escrito_contenido);

            // Extraer variables faltantes del contenido guardado
            const missing = extractVars(mov.escrito_contenido);
            missingVarsRef.current = missing || [];
            setMissingVars(missing || []);

            if (missing?.length) {
                setSnackbar({ open: true, msg: "Faltan datos: " + missing.join(", "), severity: "warning" });
            }

            setEditorKey(prev => prev + 1);
        } else if (mov.id_escrito && tipoReqEscrito(mov.tipo)) {
            // No tiene escrito guardado pero tiene ID de escrito, cargar template
            try {
                const html = await obtenerEscritoGenerado(mov.id_escrito, idExpediente);
                escritoTemplateRef.current = html || '';
                const rawExp = await fetchExpedienteVarsFlexible(idExpediente);
                expedienteVarsRef.current = buildGlossaryFromExpediente(rawExp, { ...mov, usuario });
                const res = replaceVarsInHtml(html || '', expedienteVarsRef.current);
                setEscritoContenido(res.html);
                missingVarsRef.current = res.missing || [];
                setMissingVars(res.missing || []);
                setEditorKey(prev => prev + 1);

                if (res.missing?.length) {
                    setSnackbar({ open: true, msg: "Faltan datos: " + res.missing.join(", "), severity: "warning" });
                }
            } catch (error) {
                setEscritoContenido("");
                setSnackbar({ open: true, msg: "No se pudo cargar el escrito.", severity: "error" });
            }
        } else {
            setEscritoContenido("");
            missingVarsRef.current = [];
            setMissingVars([]);
        }

        setEmbargoValidado(false);
        setRedactarEnBlanco(false);
        setDialogOpen(true);
    };

    const saveEdit = async () => {
        // Validaciones básicas
        const descRequerida = !isGenCartaPoder(editData.tipo) && !isEmbargo(editData.tipo);
        if (!editData.fecha || !editData.tipo || (descRequerida && !String(editData.descripcion || "").trim())) {
            setSnackbar({ open: true, msg: "Completá fecha, tipo y descripción.", severity: "error" });
            return;
        }
        // Validación específica de EMBARGO: estado y monto requeridos
        if (isEmbargo(editData.tipo)) {
            const faltaEstado = !editData.embargoEstado;
            const faltaMonto = editData.embargoMonto == null || editData.embargoMonto === '' || isNaN(Number(editData.embargoMonto));
            const faltaFecha = !editData.embargoFecha;
            if (faltaEstado || faltaMonto || faltaFecha) {
                setEmbargoValidado(true);
                return;
            }
        }
        if (tipoReqEscrito(editData.tipo) && !editData.escrito) {
            setSnackbar({ open: true, msg: "Seleccioná un escrito.", severity: "error" });
            return;
        }
        if (tipoReqPdf(editData.tipo) && !adjuntoBase64) {
            setSnackbar({ open: true, msg: "Adjuntá el PDF requerido.", severity: "error" });
            return;
        }
        if (!isNew && !perm.update) {
            setSnackbar({ open: true, msg: "No tenés permiso para actualizar.", severity: "error" });
            return;
        }
        if (isNew && !perm.insert) {
            setSnackbar({ open: true, msg: "No tenés permiso para crear.", severity: "error" });
            return;
        }

        // ⭐ VALIDACIÓN 6B: Fecha futura (solo si no fue confirmada)
        if (!fechaFuturaConfirmada) {
            const fechaIngresada = parseFechaAny(editData.fecha);
            const hoy = new Date();
            hoy.setHours(0, 0, 0, 0);

            if (fechaIngresada && fechaIngresada > hoy) {
                const confirmar = await new Promise(resolve => {
                    confirmResolveRef.current = resolve;
                    setConfirmDlg({
                        open: true,
                        title: '⚠️ Fecha Futura',
                        content: `La fecha ingresada (${formatFecha(editData.fecha)}) es posterior a la fecha actual.\n\n¿Estás seguro de que querés continuar?`,
                        confirmText: 'Sí, continuar',
                        cancelText: 'Cancelar'
                    });
                });

                if (!confirmar) return;

                // ⭐ Marcar como confirmado
                setFechaFuturaConfirmada(true);
            }
        }

        // ⭐ VALIDACIÓN 6A: Duplicados (solo al crear)
        if (isNew && !duplicadoConfirmado) {
            const fechaIngresada = parseFechaAny(editData.fecha);

            const similares = movimientos.filter(m => {
                // Comparar fecha (mismo día)
                const fechaMov = parseFechaAny(m.fechaRaw || m.fecha);
                const mismaFecha = fechaMov && fechaIngresada &&
                    fechaMov.getDate() === fechaIngresada.getDate() &&
                    fechaMov.getMonth() === fechaIngresada.getMonth() &&
                    fechaMov.getFullYear() === fechaIngresada.getFullYear();

                // Comparar tipo
                const mismoTipo = keyify(m.tipo) === keyify(editData.tipo);

                // Comparar descripción (similitud básica)
                const desc1 = norm(plainText(m.descripcion || ""));
                const desc2 = norm(plainText(editData.descripcion || ""));

                // Si las descripciones son muy cortas, comparar exacto
                if (desc2.length < 10) {
                    return mismaFecha && mismoTipo && desc1 === desc2;
                }

                // Si son más largas, buscar coincidencia parcial (70% de palabras)
                const palabras1 = desc1.split(/\s+/).filter(p => p.length > 3);
                const palabras2 = desc2.split(/\s+/).filter(p => p.length > 3);

                if (palabras2.length === 0) return mismaFecha && mismoTipo;

                const coinciden = palabras2.filter(p => palabras1.includes(p)).length;
                const similitud = coinciden / palabras2.length;

                return mismaFecha && mismoTipo && similitud >= 0.7;
            });

            if (similares.length > 0) {
                const ejemplo = similares[0];
                const confirmar = await new Promise(resolve => {
                    confirmResolveRef.current = resolve;
                    setConfirmDlg({
                        open: true,
                        title: '⚠️ Posible Duplicado',
                        content: `Ya existe un movimiento similar:\n\n` +
                            `📅 Fecha: ${formatFecha(ejemplo.fechaRaw || ejemplo.fecha)}\n` +
                            `📋 Tipo: ${displayTipoLabel(ejemplo.tipo)}\n` +
                            `📝 Descripción: ${plainText(ejemplo.descripcion || "").slice(0, 100)}...\n\n` +
                            `¿Querés crear este movimiento de todas formas?`,
                        confirmText: 'Sí, crear',
                        cancelText: 'Cancelar'
                    });
                });

                if (!confirmar) return;

                setDuplicadoConfirmado(true);
            }
        }

        // ⭐ CARTA PODER → Enviar al Panel de Firma del Fiscal (no crea movimiento)
        if (isNew && isGenCartaPoder(editData.tipo)) {
            setSaving(true);
            try {
                // Tomar HTML actual del editor
                let finalHtml = escritoContenido || '';
                if (editorApiRef.current?.getContent) {
                    try { finalHtml = editorApiRef.current.getContent(); } catch { }
                }
                // Reemplazar variables
                const allVars = { ...expedienteVarsRef.current, ...varsOverrideRef.current };
                const { html: htmlConVars } = replaceVarsInHtml(finalHtml, allVars);

                const res = await panelFirmaSolicitarJWT({
                    idExpediente,
                    tipo: 'CARTA_PODER',
                    idEscrito: editData.idEscrito || null,
                    contenidoHtml: htmlConVars,
                });
                if (res?.ok) {
                    setSnackbar({ open: true, msg: `Carta Poder ${res.numero || ''} enviada al Fiscal para firma.`, severity: "success" });
                    setDialogOpen(false);
                    setAdjuntoNombre("");
                    setAdjuntoBase64("");
                } else {
                    setSnackbar({ open: true, msg: res?.mensaje || "Error al solicitar firma.", severity: "error" });
                }
            } catch (e) {
                setSnackbar({ open: true, msg: "Error de red al solicitar firma.", severity: "error" });
            } finally {
                setSaving(false);
            }
            return;
        }

        // ✅ Continuar con el guardado normal
        setSaving(true);

        // Buscar el objeto escrito seleccionado (si hay)
        let escritoObj = null;
        if (editData.escrito) {
            const idSel = String(editData.escrito);
            escritoObj = (escritos || []).find(e =>
                String(e.Id || e.id) === idSel
            );
        }

        // Determinar el tipo a enviar a la API
        // Si es "MIS ESCRITOS", enviarlo tal cual para que la API guarde Id_Categoria
        const esMisEscritos = isTipo(editData.tipo, 'MIS ESCRITOS');
        const tipoFinal = esMisEscritos
            ? 'MIS ESCRITOS'
            : resolverTipoMovimiento(editData.tipo, escritoObj, tipoExpediente);

        const fechaConHora = /\d{2}:\d{2}/.test(editData.fecha)
            ? editData.fecha
            : `${editData.fecha} ${new Date().toTimeString().slice(0, 8)}`;

        // tomar el HTML actual del editor si está montado
        if (editorApiRef.current?.getContent) {
            try {
                const currentHtml = editorApiRef.current.getContent();
                setEscritoContenido(currentHtml);
            } catch { }
        }

        // Variables del movimiento actual: el glosario se armó cuando el usuario
        // seleccionó el escrito, pero la descripción/fojas pueden haberse tipeado
        // DESPUÉS — refrescamos esas vars con el state actual del dialog.
        // FOJAS se mapea también a NUMERO_RECIBO porque en el sistema viejo el
        // número de recibo se carga en ese mismo campo (queda como string).
        const movVars = {
            DESCRIPCION_MOV: editData.descripcion || '',
            Descripcion_Mov: editData.descripcion || '',
            FOJAS: editData.fojas || '',
            CANTIDAD_FOJAS: editData.fojas || '',
            Cantidad_Fojas: editData.fojas || '',
            NUMERO_RECIBO: editData.fojas || '',
            Numero_Recibo: editData.fojas || '',
            RECIBO: editData.fojas || '',
            FECHA_MOVIMIENTO: editData.fecha || '',
            Fecha_Mov: editData.fecha || '',
        };
        // merge final (expediente + movimiento actual + overrides del usuario)
        const allVars = { ...expedienteVarsRef.current, ...movVars, ...varsOverrideRef.current };
        let finalHtml = replaceVarsInHtml(escritoContenido || '', allVars).html;

        // (opcional) avisar si aún quedaron llaves
        const unresolved = extractVars(finalHtml);
        if (unresolved.length) {
            setSnackbar({ open: true, severity: 'warning', msg: 'Quedaron sin completar: ' + unresolved.join(', ') });
        }

        // Fojas: si es administrativo y hay fojas, appendear a descripción
        let descFinal = isEmbargo(editData.tipo) ? (editData.embargoEstado || "") : (editData.descripcion || "");
        if (!isEmbargo(editData.tipo) && tipoExpediente !== "judicial" && editData.fojas && editData.fojas.trim()) {
            descFinal = descFinal + " (fs. " + editData.fojas.trim() + ")";
        }

        // usar finalHtml como snapshot "inalterable" del movimiento
        const payload = {
            ...editData,
            tipo: tipoFinal,
            descripcion: descFinal,
            fecha: fechaConHora,
            usuario,
            idExpediente,
            escritoContenido: finalHtml,
            adjuntoNombre: adjuntoNombre || null,
            adjuntoBase64: adjuntoBase64 || null,
            // Campos de embargo
            ...(isEmbargo(editData.tipo) ? {
                embargoEstado: editData.embargoEstado || null,
                embargoFecha: editData.embargoFecha || null,
                embargoMonto: editData.embargoMonto != null ? editData.embargoMonto : null,
                embargoFolioDepositoJudicial: editData.embargoFolioDepositoJudicial || null,
                embargoBancoEmbargante: editData.embargoBancoEmbargante || null,
                embargoNumTransaccion: editData.embargoNumTransaccion || null,
                embargoChequeJudicial: editData.embargoChequeJudicial || null,
            } : {}),
        };

        try {
            if (isNew) {
                const res = await crearMovimientoJWT(idExpediente, payload);
                if (res?.ok) {
                    // Persistir descripción en historial local (Autocomplete del campo).
                    // No guardamos descripciones de Carta Poder ni Embargo porque tienen
                    // formato automático que no aporta como sugerencia.
                    if (!isGenCartaPoder(editData.tipo) && !isEmbargo(editData.tipo)) {
                        addDescripcionHistorial(editData.descripcion || '');
                        setDescripcionesHistorial(loadDescripcionesHistorial());
                    }
                    await recargarMovimientos();
                    setHighlightedId(res.id);
                    setTimeout(() => setHighlightedId(null), 2000);
                    setSnackbar({ open: true, msg: "Movimiento creado.", severity: "success" });
                    if (isEmbargo(editData.tipo) && onEmbargoChange) onEmbargoChange();
                    setDialogOpen(false);
                    setAdjuntoNombre("");
                    setAdjuntoBase64("");
                } else {
                    setSnackbar({ open: true, msg: res?.mensaje || "Error al crear movimiento.", severity: "error" });
                }
            } else {
                const res = await editarMovimientoJWT(idExpediente, editData.id, payload);
                if (res?.ok) {
                    if (!isGenCartaPoder(editData.tipo) && !isEmbargo(editData.tipo)) {
                        addDescripcionHistorial(editData.descripcion || '');
                        setDescripcionesHistorial(loadDescripcionesHistorial());
                    }
                    await recargarMovimientos();
                    setHighlightedId(editData.id);
                    setTimeout(() => setHighlightedId(null), 2000);
                    setSnackbar({ open: true, msg: "Movimiento actualizado.", severity: "success" });
                    if (isEmbargo(editData.tipo) && onEmbargoChange) onEmbargoChange();
                    setDialogOpen(false);
                    setAdjuntoNombre("");
                    setAdjuntoBase64("");
                } else {
                    setSnackbar({ open: true, msg: res?.mensaje || "Error al actualizar.", severity: "error" });
                }
            }
        } catch (e) {
            setSnackbar({ open: true, msg: "Error de red al guardar.", severity: "error" });
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = (id) => {
        if (!perm.delete) { setSnackbar({ open: true, msg: "No tenés permiso para eliminar.", severity: "warning" }); return; }
        const mov = movimientos.find(m => m.id === id);
        setDeleteId(id);
        setDeleteInfo({
            fecha: formatFecha(mov?.fecha),
            tipo: displayTipoLabel(mov?.tipo || ""),
            responsable: mov?.responsable || "",
            resumen: String(plainText(mov?.descripcion || "")).slice(0, 300)
        });
        setDialogDeleteOpen(true);
    };

    const confirmDelete = async () => {
        try {
            const movToDelete = movimientos.find(m => m.id === deleteId);
            const res = await eliminarMovimientoJWT(idExpediente, deleteId);
            if (res?.ok) {
                setMovimientos(prev => prev.filter(m => m.id !== deleteId));
                setSnackbar({ open: true, msg: "Movimiento eliminado.", severity: "success" });
                if (movToDelete && isEmbargo(movToDelete.tipo) && onEmbargoChange) onEmbargoChange();
            } else setSnackbar({ open: true, msg: res?.mensaje || "Error al eliminar.", severity: "error" });
        } catch { setSnackbar({ open: true, msg: "Error de red al eliminar.", severity: "error" }); }
        finally { setDialogDeleteOpen(false); }
    };

    const handleCambiarEstado = async (nuevo) => {
        try {
            const r = await ETAPAS_API.cambiar(idExpediente, nuevo, estadoActual);
            if (!r?.ok) throw new Error(r?.mensaje || "No se pudo cambiar el estado.");
            setEstadoActual(normOne(nuevo));
            setSnackbar({ open: true, severity: "success", msg: `Estado actualizado a ${nuevo.nombre || nuevo.label}.` });
        } catch (e) {
            setSnackbar({ open: true, severity: "error", msg: e.message || "Error al cambiar estado." });
        } finally {
            await recargarMovimientos();
            setOpenEstado(false);
        }
    };

    const buildFileUrl = (raw) => {
        if (!raw) return "";
        if (/^https?:\/\//i.test(raw) || raw.startsWith("//")) return raw;

        const path = raw.startsWith("/") ? raw : `/${raw}`;

        const { protocol, hostname, port } = window.location;
        const devPorts = new Set(["3000", "5173", "5174"]);
        const portPart = devPorts.has(port) ? "" : (port ? `:${port}` : "");

        return `${protocol}//${hostname}${portPart}${path}`;
    };

    const extractPdfHref = (html) => {
        if (!html) return null;
        const m = String(html).match(/href=["']([^"']+\.pdf)["']/i);
        return m ? m[1] : null;
    };

    // Determina si el movimiento apunta a un expediente vinculado
    const esExpVinculado = (mov) => {
        const url = String(mov.adjunto_url || "");
        return url.startsWith("expediente:") || url.endsWith(":exp");
    };

    const esResolucionVinculada = (mov) => String(mov.url_vinculado || "").startsWith("resolucion:");

    // Movimiento "Repositorio": URL externa (link al repositorio del PJ u otro)
    // guardada como "repositorio:https://...". Se abre en pestaña nueva.
    const esRepositorio = (mov) => String(mov.url_vinculado || "").startsWith("repositorio:");
    const urlRepositorio = (mov) => String(mov.url_vinculado || "").replace(/^repositorio:/, "").trim();

    // Vinculado del sistema viejo: URL_Vinculado guardado como filename plano,
    // sin prefijo "vinculado:" / "expediente:" / "resolucion:"
    const esVinculadoLegacy = (mov) => {
        const u = String(mov.url_vinculado || "").trim();
        if (!u) return false;
        if (u.startsWith("vinculado:") || u.startsWith("expediente:") || u.startsWith("resolucion:") || u.startsWith("repositorio:")) return false;
        return /\.(pdf|jpg|jpeg|png|gif|docx?|xlsx?)$/i.test(u);
    };

    // Determina si un movimiento tiene algo para abrir/ver (no exp)
    const tieneAdjunto = (mov) =>
        esRepositorio(mov) ||
        esResolucionVinculada(mov) ||
        esVinculadoLegacy(mov) ||
        (mov.adjunto_url && !esExpVinculado(mov)) ||
        (mov.escrito_contenido && String(mov.escrito_contenido).trim()) ||
        hasEscrito(mov.escrito);

    const abrirEscrito = async (mov) => {
        // -3) Repositorio → abrir la URL externa en pestaña nueva
        if (esRepositorio(mov)) {
            const u = urlRepositorio(mov);
            if (u) window.open(u, "_blank", "noopener,noreferrer");
            return;
        }
        // -2) Resolución vinculada → abrir PDF de la resolución
        if (esResolucionVinculada(mov)) {
            const resId = String(mov.url_vinculado).replace("resolucion:", "");
            try {
                const { nodeApiPath } = await import("../../utils/paths");
                const token = localStorage.getItem("SIGIF_JWT_TOKEN");
                const base = nodeApiPath(`expedientes/${idExpediente}/resoluciones/${resId}/download`);
                const url = token ? `${base}?token=${encodeURIComponent(token)}` : base;
                setPreviewTitle(mov.descripcion || "PDF Resolución");
                setPreviewPdfUrl(url);
                setPreviewHtml("");
                setPreviewOpen(true);
            } catch {
                setSnackbar({ open: true, severity: "warning", msg: "No se pudo abrir el PDF de la resolución." });
            }
            return;
        }

        // -1) Expediente vinculado → abrir en nueva pestaña
        if (mov.adjunto_url && String(mov.adjunto_url).startsWith("expediente:")) {
            // Formato: "expediente:judicial:123" o "expediente:admin:456"
            const parts = String(mov.adjunto_url).split(":");
            const expTipo = parts[1] || "judicial";
            const expId = parts[2] || "";
            if (expId) {
                const tipo = expTipo.startsWith("admin") ? "administrativo" : "judicial";
                window.open(`/expediente/${tipo}/${expId}`, "_blank", "noopener,noreferrer");
            }
            return;
        }

        // 0a) Vinculado del sistema viejo: URL_Vinculado guardado como filename plano
        //     (ej: "V_2026_551274.pdf"). Buscamos el id en la lista de vinculados del
        //     expediente matcheando por nombre y reusamos el flujo de descarga JWT.
        if (esVinculadoLegacy(mov)) {
            try {
                const list = await expedienteVinculadosJWT(idExpediente);
                const vincs = list?.vinculados || list?.data || [];
                const fname = String(mov.url_vinculado || "").trim();
                const found = vincs.find(v => {
                    const u = String(v.url_vinculado || v.URL_Vinculado || "").trim();
                    return u === fname || u.endsWith("/" + fname) || u.endsWith("\\" + fname);
                });
                if (!found) {
                    setSnackbar({ open: true, severity: "warning", msg: "El vinculado referenciado ya no existe." });
                    return;
                }
                const idVinc = found.id || found.Id;
                const ext = (fname.match(/\.([a-z0-9]+)$/i)?.[1] || "").toLowerCase();
                const { nodeApiPath } = await import("../../utils/paths");
                const token = localStorage.getItem("SIGIF_JWT_TOKEN");
                const base = nodeApiPath(`expedientes/${idExpediente}/vinculados/${idVinc}/download`);
                const url = token ? `${base}?token=${encodeURIComponent(token)}` : base;
                setPreviewVinculado({ id: idVinc, ext });
                if (ext === "pdf") {
                    setPreviewTitle(mov.descripcion || "PDF Vinculado");
                    setPreviewPdfUrl(url);
                    setPreviewHtml("");
                    setPreviewOpen(true);
                } else if (["jpg", "jpeg", "png", "gif"].includes(ext)) {
                    setPreviewTitle(mov.descripcion || "Imagen");
                    setPreviewPdfUrl("");
                    setPreviewHtml(`<div style="text-align:center;padding:16px"><img src="${url}" style="max-width:100%;max-height:70vh;border-radius:8px" /></div>`);
                    setPreviewOpen(true);
                } else {
                    setPreviewVinculado(null);
                    try { await downloadVinculadoJWT(idExpediente, idVinc, mov.descripcion || "archivo"); }
                    catch { setSnackbar({ open: true, severity: "warning", msg: "No se pudo descargar el archivo." }); }
                }
            } catch {
                setSnackbar({ open: true, severity: "warning", msg: "No se pudo abrir el vinculado." });
            }
            return;
        }

        // 0) Vinculado adjunto (creado desde tab Vinculados con "Generar movimiento")
        //    Formato: "vinculado:{idVinculado}:{ext}" (ej: "vinculado:123:pdf")
        if (mov.adjunto_url && String(mov.adjunto_url).startsWith("vinculado:")) {
            const parts = String(mov.adjunto_url).split(":");
            const idVinculado = parts[1];
            const ext = (parts[2] || "").toLowerCase();

            // Legacy: vinculado:*:exp → buscar datos del vinculado para abrir expediente
            if (ext === "exp") {
                try {
                    const data = await getVinculadoJWT(idExpediente, idVinculado);
                    const urlVinc = data?.vinculado?.url_vinculado || "";
                    if (urlVinc.includes(":")) {
                        const [expTipo, expId] = urlVinc.split(":");
                        const tipo = expTipo.startsWith("admin") ? "administrativo" : "judicial";
                        window.open(`/expediente/${tipo}/${expId}`, "_blank", "noopener,noreferrer");
                    }
                } catch {
                    setSnackbar({ open: true, severity: "warning", msg: "No se pudo obtener datos del expediente vinculado." });
                }
                return;
            }

            // Verificar que el vinculado aún exista antes de abrir
            try {
                const check = await getVinculadoJWT(idExpediente, idVinculado);
                if (!check?.ok || !check?.vinculado) {
                    setVinculadoEliminadoOpen(true);
                    return;
                }
            } catch {
                setVinculadoEliminadoOpen(true);
                return;
            }

            const { nodeApiPath } = await import("../../utils/paths");
            const token = localStorage.getItem("SIGIF_JWT_TOKEN");
            const base = nodeApiPath(`expedientes/${idExpediente}/vinculados/${idVinculado}/download`);
            const url = token ? `${base}?token=${encodeURIComponent(token)}` : base;

            setPreviewVinculado({ id: idVinculado, ext });

            if (ext === "pdf") {
                setPreviewTitle(mov.descripcion || "PDF Vinculado");
                setPreviewPdfUrl(url);
                setPreviewHtml("");
                setPreviewOpen(true);
            } else if (["jpg", "jpeg", "png", "gif"].includes(ext)) {
                setPreviewTitle(mov.descripcion || "Imagen");
                setPreviewPdfUrl(""); // No iframe
                setPreviewHtml(`<div style="text-align:center;padding:16px"><img src="${url}" style="max-width:100%;max-height:70vh;border-radius:8px" /></div>`);
                setPreviewOpen(true);
            } else {
                setPreviewVinculado(null);
                // Word/Excel/otros → descargar directo
                try {
                    await downloadVinculadoJWT(idExpediente, idVinculado, mov.descripcion || "archivo");
                } catch {
                    setSnackbar({ open: true, severity: "warning", msg: "No se pudo descargar el archivo." });
                }
            }
            return;
        }
        setPreviewVinculado(null);

        // 1) PDF vinculado desde PDFBase64 (movimientos tipo 64 de carga masiva / carta poder)
        if (mov.adjunto_url === "pdf" && mov.id) {
            try {
                const blobUrl = await movimientoPdfJWT(idExpediente, mov.id);
                if (blobUrl) {
                    setPreviewTitle(mov.descripcion || "PDF Vinculado");
                    setPreviewPdfUrl(blobUrl);
                    setPreviewHtml("");
                    setPreviewOpen(true);
                    return;
                }
            } catch (err) {
                console.error("Error al obtener PDF del movimiento:", err);
            }
            setSnackbar({ open: true, severity: "warning", msg: "No se pudo cargar el PDF vinculado." });
            return;
        }

        // 2) Contenido HTML del escrito guardado inline
        if (mov.escrito_contenido && String(mov.escrito_contenido).trim()) {
            const href = extractPdfHref(mov.escrito_contenido);
            if (href) {
                const url = buildFileUrl(href);
                setPreviewTitle(mov.adjunto_nombre || "Adjunto PDF");
                setPreviewPdfUrl(url);
                setPreviewHtml("");
                setPreviewOpen(true);
                return;
            }
            setPreviewTitle(mov.escrito || "Escrito");
            setPreviewPdfUrl("");
            setPreviewHtml(mov.escrito_contenido);
            setPreviewOpen(true);
            return;
        }

        // 3) Escrito por ID (fetch desde API)
        if (hasEscrito(mov.escrito) && mov.id_escrito) {
            const html = await obtenerEscritoGenerado(mov.id_escrito, idExpediente);
            setPreviewTitle(mov.escrito || "Escrito");
            setPreviewPdfUrl("");
            if (html && String(html).trim()) {
                setPreviewHtml(html);
            } else {
                // Movimiento del sistema anterior: el contenido se generaba con SQL Reporting
                // Services en runtime y nunca se guardaba en la DB.
                const plantilla = String(mov.escrito || "Sin nombre")
                    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
                setPreviewHtml(
                    `<div style="padding:32px 24px;text-align:center;font-family:system-ui,sans-serif;color:#555">
                        <div style="font-size:48px;margin-bottom:12px">📜</div>
                        <div style="font-size:16px;font-weight:600;color:#333;margin-bottom:8px">
                            Escrito generado en el sistema anterior
                        </div>
                        <div style="font-size:14px;margin-bottom:12px">
                            Plantilla: <strong>${plantilla}</strong>
                        </div>
                        <div style="font-size:13px;color:#888;max-width:480px;margin:0 auto;line-height:1.5">
                            Los movimientos del sistema legado se renderizaban con SQL Reporting Services
                            al momento de abrirse y no almacenaban el contenido. Por eso el detalle ya
                            no es recuperable.
                        </div>
                    </div>`
                );
            }
            setPreviewOpen(true);
            return;
        }

        // 4) URL legacy (ASP.NET / archivo externo) — fallback
        if (mov.adjunto_url) {
            const url = buildFileUrl(mov.adjunto_url);
            setPreviewTitle(mov.adjunto_nombre || "Adjunto PDF");
            setPreviewPdfUrl(url);
            setPreviewHtml("");
            setPreviewOpen(true);
            return;
        }

        setSnackbar({ open: true, severity: "info", msg: "Este movimiento no tiene escrito adjunto." });
    };

    const showHeaderSkeleton = loading || !permReady || estadoLoading;

    const optLabel = (o) => {
        const raw =
            typeof o === "string"
                ? o
                : (o?.nombre ?? o?.Nombre ?? o?.label ?? o?.Etapa ?? o?.Estado ?? "");
        const small = new Set(["de", "del", "y", "en", "la", "las", "los", "con", "para", "por", "al", "a", "o", "u"]);
        return String(raw || "")
            .toLowerCase()
            .split(/\s+/)
            .map((w, i) => (i > 0 && small.has(w) ? w : w.charAt(0).toUpperCase() + w.slice(1)))
            .join(" ");
    };

    function formatFechaHora(v) {
        if (!v) return "";
        const s = String(v).trim();

        const m = s.match(/^(\d{4})-(\d{2})-(\d{2})\s+(\d{2}):(\d{2})(?::(\d{2}))?$/);
        if (m) {
            const [, yyyy, mm, dd, hh, mi] = m;
            const showTime = !(hh === "00" && mi === "00");
            return showTime ? `${dd}/${mm}/${yyyy} ${hh}:${mi}` : `${dd}/${mm}/${yyyy}`;
        }

        const d = parseFechaAny(s);
        if (!d) return s;
        const dd = String(d.getDate()).padStart(2, "0");
        const mm = String(d.getMonth() + 1).padStart(2, "0");
        const yyyy = d.getFullYear();
        const hh = String(d.getHours()).padStart(2, "0");
        const mi = String(d.getMinutes()).padStart(2, "0");
        const showTime = !(hh === "00" && mi === "00");
        return showTime ? `${dd}/${mm}/${yyyy} ${hh}:${mi}` : `${dd}/${mm}/${yyyy}`;
    }

    const cap = (s) => {
        const raw = String(s ?? "");
        const small = new Set(["de", "del", "y", "en", "la", "las", "los", "con", "para", "por", "al", "a", "o", "u"]);
        const words = raw.split(/\s+/);
        const lower = raw.toLowerCase().split(/\s+/);

        return lower
            .map((w, i) => {
                if (/^([a-z]\.){2,}[a-z]?\.?$/.test(w)) return w.toUpperCase();
                if (/\d/.test(w) || /[-/]/.test(w)) return words[i]; // preservar original
                if (/^[A-Z]{2,}$/.test(words[i])) return words[i]; // preservar siglas (PDF, HTML, etc.)
                return (i > 0 && small.has(w)) ? w : w.charAt(0).toUpperCase() + w.slice(1);
            })
            .join(" ");
    };

    // RENDER
    return (
        <Paper elevation={2} style={{ padding: 16, borderRadius: 4 }}>
            {/* ⭐ Banner Estado del Proceso - SOLO PARA JUDICIALES */}
            {tipoExpediente === "judicial" && !estadoLoading && (
                <Paper elevation={2} style={{
                    padding: "10px 20px",
                    marginBottom: 16,
                    borderRadius: 4,
                    background: getEstadoColor(estadoActual?.nombre).bg,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center"
                }}>
                    <Box display="flex" alignItems="center" gap={1.5}>
                        <Box style={{
                            width: 36,
                            height: 36,
                            borderRadius: "50%",
                            background: "rgba(255,255,255,0.2)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            marginRight: 4 // ⭐ Espacio extra después del ícono
                        }}>
                            <AssignmentIcon style={{ color: "#fff", fontSize: 20 }} />
                        </Box>
                        <Box display="flex" alignItems="baseline" gap={0.75}> {/* ⭐ Gap aumentado */}
                            <Typography variant="body2" style={{
                                color: "rgba(255,255,255,0.9)",
                                fontWeight: 600,
                                fontSize: 12,
                                marginRight: 4 // ⭐ Espacio después de "Estado del proceso:"
                            }}>
                                Estado Procesal:
                            </Typography>
                            <Typography variant="body1" style={{
                                color: "#fff",
                                fontWeight: 700,
                                fontSize: 15
                            }}>
                                {estadoActual?.nombre ? optLabel(estadoActual) : "Sin asignar"}
                            </Typography>
                        </Box>
                    </Box>
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={() => setOpenEstado(true)}
                        style={{
                            color: "#fff",
                            borderColor: "rgba(255,255,255,0.6)",
                            textTransform: "none",
                            fontWeight: 600,
                            padding: "4px 14px",
                            fontSize: 13
                        }}
                    >
                        Cambiar
                    </Button>
                </Paper>
            )}
            {/* Dialog para cambiar estado */}
            <Dialog open={openEstado} onClose={() => setOpenEstado(false)} maxWidth="sm" fullWidth>
                <DialogTitle style={{
                    background: getEstadoColor(estadoActual?.nombre).bg,
                    color: "#fff",
                    fontWeight: 700
                }}>
                    Cambiar Estado Procesal
                </DialogTitle>
                <DialogContent style={{ paddingTop: 20 }}>
                    <Autocomplete
                        autoFocus
                        options={estadosDisponibles || []}
                        getOptionLabel={optLabel}
                        isOptionEqualToValue={(o, v) => String(o?.id) === String(v?.id)}
                        renderOption={(props, option) => {
                            const { key, ...rest } = props;
                            const isCurrent = estadoActual && String(option?.id) === String(estadoActual?.id);
                            const colors = getEstadoColor(option?.nombre);
                            return (
                                <li key={key} {...rest}>
                                    <Box style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 8,
                                    }}>
                                        <Box style={{
                                            width: 12,
                                            height: 12,
                                            borderRadius: "50%",
                                            background: colors.bg,
                                            border: isCurrent ? "2px solid #1976d2" : "none"
                                        }} />
                                        <span style={{ fontWeight: isCurrent ? 700 : 400 }}>
                                            {optLabel(option)}
                                        </span>
                                    </Box>
                                </li>
                            );
                        }}
                        value={estadoActual ? estadosDisponibles.find(e => String(e.id) === String(estadoActual.id)) || null : null}
                        onChange={(_, val) => {
                            if (val) {
                                handleCambiarEstado(val);
                            }
                        }}
                        renderInput={(params) => (
                            <TextField {...params} label="Seleccionar nuevo estado" variant="outlined" fullWidth />
                        )}
                    />
                </DialogContent>
                <DialogActions style={{ padding: "12px 24px" }}>
                    <Button onClick={() => setOpenEstado(false)} color="secondary">
                        Cancelar
                    </Button>
                </DialogActions>
            </Dialog>
            <Box className={classes.headerBar}>
                <Box className={classes.headerLeft}>
                    {showHeaderSkeleton ? (
                        <>
                            <Skeleton variant="rectangular" width={360} height={36} style={{ borderRadius: 4 }} />
                            <Skeleton variant="rectangular" width={280} height={36} style={{ borderRadius: 4 }} />
                        </>
                    ) : (
                        <>
                            {/* Búsqueda */}
                            <TextField
                                className={classes.searchInput}
                                variant="outlined"
                                size="small"
                                placeholder="Búsqueda libre"
                                value={q}
                                onChange={(e) => setQ(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon style={{ opacity: 0.6 }} />
                                        </InputAdornment>
                                    ),
                                    style: { borderRadius: 4 }
                                }}
                            />

                            {/* ⭐ Grupo: Acciones */}
                                <Box display="flex" alignItems="center" gap={0.5} style={{
                                    background: "#f5f5f5",
                                    borderRadius: 4,
                                    padding: "4px",
                                    border: "1px solid #e0e0e0"
                                }}>
                                    <Tooltip title="Filtros avanzados">
                                        <IconButton
                                            size="small"
                                            onClick={() => setFiltrosAbiertos(!filtrosAbiertos)}
                                            style={{
                                                background: (filtroTipo || filtroEstadoProceso || filtroVinculados || filtroFechaDesde || filtroFechaHasta) ? "#1976d2" : "transparent",
                                                color: (filtroTipo || filtroEstadoProceso || filtroVinculados || filtroFechaDesde || filtroFechaHasta) ? "#fff" : "#666"
                                            }}
                                        >
                                            <FilterListIcon />
                                        </IconButton>
                                    </Tooltip>

                                    <Tooltip title="Exportar a PDF">
                                        <IconButton
                                            size="small"
                                            onClick={exportarPDF}
                                            disabled={exportando || movimientosOrdenados.length === 0}
                                        >
                                            {exportando ? <CircularProgress size={20} /> : <GetAppIcon />}
                                        </IconButton>
                                    </Tooltip>

                                    <Divider orientation="vertical" flexItem style={{ margin: "4px 4px" }} />

                                    <Tooltip title="Vista Tabla">
                                        <IconButton
                                            size="small"
                                            onClick={() => setViewMode("table")}
                                            style={{
                                                background: viewMode === "table" ? "#1976d2" : "transparent",
                                                color: viewMode === "table" ? "#fff" : "#666"
                                            }}
                                        >
                                            <ViewListIcon />
                                        </IconButton>
                                    </Tooltip>

                                    <Tooltip title="Vista Timeline">
                                        <IconButton
                                            size="small"
                                            onClick={() => setViewMode("timeline")}
                                            style={{
                                                background: viewMode === "timeline" ? "#1976d2" : "transparent",
                                                color: viewMode === "timeline" ? "#fff" : "#666"
                                            }}
                                        >
                                            <TimelineIcon />
                                        </IconButton>
                                    </Tooltip>

                                    {/* ⭐ NUEVO: Botón Calendario */}
                                    <Tooltip title="Vista Calendario">
                                        <IconButton
                                            size="small"
                                            onClick={() => setViewMode("calendar")}
                                            style={{
                                                background: viewMode === "calendar" ? "#1976d2" : "transparent",
                                                color: viewMode === "calendar" ? "#fff" : "#666"
                                            }}
                                        >
                                            <EventIcon />
                                        </IconButton>
                                    </Tooltip>

                                    {/* ⭐ NUEVO: Botón Estadísticas */}
                                    <Tooltip title="Estadísticas">
                                        <IconButton
                                            size="small"
                                            onClick={() => setViewMode("stats")}
                                            style={{
                                                background: viewMode === "stats" ? "#1976d2" : "transparent",
                                                color: viewMode === "stats" ? "#fff" : "#666"
                                            }}
                                        >
                                            <BarChartIcon />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                        </>
                    )}
                </Box>

                {/* Botón Nuevo */}
                {showHeaderSkeleton ? (
                    <Skeleton variant="rectangular" width={190} height={36} style={{ borderRadius: 4 }} />
                ) : (
                    perm.insert && (
                        <Box style={{ display: "flex", gap: 8 }}>
                            <Button
                                variant="outlined"
                                style={{ borderRadius: 4, fontWeight: 600, textTransform: "none", marginBottom: 8, borderColor: "#1769aa", color: "#1769aa" }}
                                startIcon={<LinkIcon />}
                                onClick={() => setRepoDlg({ open: true, descripcion: "", url: "", saving: false })}
                            >
                                Repositorio
                            </Button>
                            <Button
                                variant="contained"
                                style={{ borderRadius: 4, background: "#1769aa", fontWeight: 600, textTransform: "none", marginBottom: 8, color: "#fff" }}
                                startIcon={<AddIcon />}
                                onClick={startAdd}
                            >
                                Nuevo movimiento
                            </Button>
                        </Box>
                    )
                )}
            </Box>
            {/* ⭐ Panel de filtros avanzados */}
            <Collapse in={filtrosAbiertos}>
                <Paper elevation={1} style={{ padding: 16, marginBottom: 16, background: "#f5f5f5", borderRadius: 4 }}>
                    <Grid container spacing={2} alignItems="center">
                        <Grid size={{ xs: 12, sm: 3 }}>
                            <Autocomplete
                                options={[
                                    ...tiposParaMenu.filter(t => !t.isDivider && !t.__virtual),
                                    { tipo: "VINCULADOS", labelUI: "Vinculados" },
                                    { tipo: "ESTADO PROCESO", labelUI: "Estado Proceso" },
                                ]}
                                getOptionLabel={t => cap(t.labelUI || displayTipoLabel(t.tipo))}
                                value={[...tiposParaMenu, { tipo: "VINCULADOS", labelUI: "Vinculados" }, { tipo: "ESTADO PROCESO", labelUI: "Estado Proceso" }].find(t => t.tipo === filtroTipo) || null}
                                onChange={(_, val) => setFiltroTipo(val ? val.tipo : "")}
                                renderInput={params => (
                                    <TextField {...params} label="Filtrar por tipo" variant="outlined" size="small" fullWidth />
                                )}
                            />
                        </Grid>

                        {/* ⭐ NUEVO: Filtro por Estado Proceso */}
                        <Grid size={{ xs: 12, sm: 3 }}>
                            <Autocomplete
                                options={estadosDisponibles || []}
                                getOptionLabel={optLabel}
                                value={estadosDisponibles.find(e => optLabel(e) === filtroEstadoProceso) || null}
                                onChange={(_, val) => setFiltroEstadoProceso(val ? optLabel(val) : "")}
                                renderInput={params => (
                                    <TextField {...params} label="Filtrar por estado" variant="outlined" size="small" fullWidth />
                                )}
                            />
                        </Grid>

                        <Grid size={{ xs: 12, sm: 2 }} style={{ display: "flex", alignItems: "center" }}>
                            <Chip
                                icon={<AttachFileIcon />}
                                label="Con Adjunto"
                                onClick={() => setFiltroVinculados(!filtroVinculados)}
                                color={filtroVinculados ? "primary" : "default"}
                                variant={filtroVinculados ? "filled" : "outlined"}
                                style={{ fontWeight: 600 }}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 2 }}>
                            <TextField
                                type="date"
                                label="Desde"
                                variant="outlined"
                                size="small"
                                value={filtroFechaDesde}
                                onChange={e => setFiltroFechaDesde(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                                fullWidth
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 2 }}>
                            <TextField
                                type="date"
                                label="Hasta"
                                variant="outlined"
                                size="small"
                                value={filtroFechaHasta}
                                onChange={e => setFiltroFechaHasta(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                                fullWidth
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 2 }}>
                            <Button
                                variant="outlined"
                                fullWidth
                                onClick={() => {
                                    setFiltroTipo("");
                                    setFiltroEstadoProceso("");
                                    setFiltroVinculados(false);
                                    setFiltroFechaDesde("");
                                    setFiltroFechaHasta("");
                                }}
                                disabled={!filtroTipo && !filtroEstadoProceso && !filtroVinculados && !filtroFechaDesde && !filtroFechaHasta}
                            >
                                Limpiar
                            </Button>
                        </Grid>
                    </Grid>

                    {(filtroTipo || filtroEstadoProceso || filtroVinculados || filtroFechaDesde || filtroFechaHasta) && (
                        <Box mt={2}>
                            <Typography variant="caption" color="text.secondary">
                                Mostrando {movimientosFiltrados.length} de {movimientos.length} movimientos
                            </Typography>
                        </Box>
                    )}
                </Paper>
            </Collapse>
            {permReady && (!perm.visible || !perm.select) && movimientos.length === 0 && (
                <Box mb={2}><Alert severity="warning">No tenés permiso para ver los movimientos. Es posible que el listado aparezca vacío.</Alert></Box>
            )}
            {loading ? (
                <Box textAlign="center" py={4}><CircularProgress /></Box>
            ) : viewMode === "table" ? (
                <>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>
                                    <TableSortLabel
                                        active={orderBy === "fecha"}
                                        direction={orderBy === "fecha" ? order : "asc"}
                                        onClick={() => handleSort("fecha")}
                                        style={orderBy === "fecha" ? { color: "#1976d2" } : {}}
                                    >
                                        Fecha
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell>
                                    <TableSortLabel
                                        active={orderBy === "tipo"}
                                        direction={orderBy === "tipo" ? order : "asc"}
                                        onClick={() => handleSort("tipo")}
                                        style={orderBy === "tipo" ? { color: "#1976d2" } : {}}
                                    >
                                        Tipo
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell>
                                    <TableSortLabel
                                        active={orderBy === "descripcion"}
                                        direction={orderBy === "descripcion" ? order : "asc"}
                                        onClick={() => handleSort("descripcion")}
                                        style={orderBy === "descripcion" ? { color: "#1976d2" } : {}}
                                    >
                                        Descripción
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell>Adjunto</TableCell>
                                <TableCell>
                                    <TableSortLabel
                                        active={orderBy === "responsable"}
                                        direction={orderBy === "responsable" ? order : "asc"}
                                        onClick={() => handleSort("responsable")}
                                        style={orderBy === "responsable" ? { color: "#1976d2" } : {}}
                                    >
                                        Responsable
                                    </TableSortLabel>
                                </TableCell>
                                {perm.update && <TableCell align="center">Editar</TableCell>}
                                {perm.delete && <TableCell align="center">Eliminar</TableCell>}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {movimientosPaginados.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={7} align="center" style={{ padding: 40 }}>
                                        <Typography variant="body2" color="text.secondary">
                                            {q || filtroTipo || filtroEstadoProceso || filtroVinculados || filtroFechaDesde || filtroFechaHasta
                                                ? "No se encontraron movimientos con los filtros aplicados"
                                                : "No hay movimientos para mostrar"}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                movimientosPaginados.map((mov, idx) => {
                                    const clickable = tieneAdjunto(mov) || esExpVinculado(mov);
                                    return (
                                    <TableRow
                                        key={mov.id}
                                        style={mov.id === highlightedId ? { background: "#e3f2fd", transition: "background 0.5s" } : {}}
                                        sx={{
                                            backgroundColor: mov.id !== highlightedId ? (idx % 2 === 0 ? "rgba(0,0,0,0.02)" : "transparent") : undefined,
                                            ...(clickable ? { cursor: "pointer", "&:hover": { backgroundColor: "rgba(25,118,210,0.08) !important" } } : {})
                                        }}
                                        onClick={clickable ? () => abrirEscrito(mov) : undefined}
                                    >
                                        <TableCell>
                                            {q ? highlightText(formatFechaHora(mov.fechaRaw || mov.fecha), q) : formatFechaHora(mov.fechaRaw || mov.fecha)}
                                        </TableCell>
                                        <TableCell>
                                            {q ? highlightText(cap(displayTipoLabel(mov.tipo)), q) : cap(displayTipoLabel(mov.tipo))}
                                        </TableCell>
                                        <TableCell>
                                            {q ? highlightText(cap(plainText(mov.descripcion || "")), q) : cap(plainText(mov.descripcion || ""))}
                                        </TableCell>
                                        <TableCell>
                                            {clickable ? (() => {
                                                if (esExpVinculado(mov)) return <Chip size="small" icon={<FolderIcon />} label="Expediente" color="primary" variant="filled" />;
                                                if (esResolucionVinculada(mov)) return <Chip size="small" icon={<PictureAsPdfIcon />} label="Resolución" color="secondary" variant="filled" />;
                                                const url = String(mov.adjunto_url || "");
                                                if (url.startsWith("vinculado:")) {
                                                    const ext = (url.split(":")[2] || "").toLowerCase();
                                                    if (ext === "pdf") return <Chip size="small" icon={<PictureAsPdfIcon />} label="PDF" color="error" variant="filled" />;
                                                    if (["jpg","jpeg","png","gif"].includes(ext)) return <Chip size="small" icon={<ImageIcon />} label="Imagen" color="success" variant="filled" />;
                                                    if (["doc","docx"].includes(ext)) return <Chip size="small" icon={<DescriptionIcon />} label="Word" color="primary" variant="filled" />;
                                                    if (["xls","xlsx"].includes(ext)) return <Chip size="small" icon={<DescriptionIcon />} label="Excel" color="success" variant="filled" />;
                                                    return <Chip size="small" icon={<AttachFileIcon />} label="Archivo" variant="filled" />;
                                                }
                                                if (url === "pdf") return <Chip size="small" icon={<PictureAsPdfIcon />} label="PDF" color="error" variant="filled" />;
                                                if (mov.escrito_contenido || hasEscrito(mov.escrito)) return <Chip size="small" icon={<AssignmentIcon />} label="Escrito" color="info" variant="filled" />;
                                                if (url) return <Chip size="small" icon={<AttachFileIcon />} label="Adjunto" variant="filled" />;
                                                return <Chip size="small" icon={<VisibilityIcon />} label="Ver" color="primary" variant="filled" />;
                                            })() : <span style={{ opacity: .5 }}>-</span>}
                                        </TableCell>
                                        <TableCell>
                                            {q ? highlightText(mov.responsable, q) : mov.responsable}
                                        </TableCell>
                                        {perm.update && (
                                            <TableCell align="center">
                                                {!isTipo(mov.tipo, "ESTADO PROCESO") && !isTipo(mov.tipo, "CARTA PODER") && puedeModificarMov(mov) ? (
                                                    <Tooltip title="Editar">
                                                        <span>
                                                            <IconButton onClick={(e) => { e.stopPropagation(); startEdit(mov.id); }} size="small">
                                                                <EditIcon style={{ color: "#1976d2" }} />
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>
                                                ) : (
                                                    <span style={{ opacity: 0.3 }}>-</span>
                                                )}
                                            </TableCell>
                                        )}
                                        {perm.delete && (
                                            <TableCell align="center">
                                                {puedeModificarMov(mov) ? (
                                                    <Tooltip title="Eliminar">
                                                        <span>
                                                            <IconButton onClick={(e) => { e.stopPropagation(); handleDelete(mov.id); }} size="small">
                                                                <DeleteIcon style={{ color: "#d32f2f" }} />
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>
                                                ) : (
                                                    <span style={{ opacity: 0.3 }}>-</span>
                                                )}
                                            </TableCell>
                                        )}
                                    </TableRow>
                                    );
                                })
                            )}
                        </TableBody>
                    </Table>

                    {movimientosOrdenados.length > 0 && (
                        <TablePagination
                            component="div"
                            count={movimientosOrdenados.length}
                            page={page}
                            onPageChange={(_, newPage) => setPage(newPage)}
                            rowsPerPage={rowsPerPage}
                            onRowsPerPageChange={(e) => {
                                setRowsPerPage(parseInt(e.target.value, 10));
                                setPage(0);
                            }}
                            rowsPerPageOptions={[10, 25, 50, 100]}
                            labelRowsPerPage="Filas por página:"
                            labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
                        />
                    )}
                </>
            ) : viewMode === "timeline" ? (
                <>
                    <Box p={2}>
                        {movimientosPaginados.length === 0 ? (
                            <Typography variant="body2" color="text.secondary" align="center" style={{ padding: 40 }}>
                                {q || filtroTipo || filtroEstadoProceso || filtroVinculados || filtroFechaDesde || filtroFechaHasta
                                    ? "No se encontraron movimientos con los filtros aplicados"
                                    : "No hay movimientos para mostrar"}
                            </Typography>
                        ) : (
                            <Box position="relative">
                                <Box
                                    position="absolute"
                                    left={20}
                                    top={0}
                                    bottom={0}
                                    width={2}
                                    bgcolor="#e0e0e0"
                                />

                                {movimientosPaginados.map((mov, idx) => (
                                    <Box
                                        key={mov.id}
                                        position="relative"
                                        pl={6}
                                        pb={3}
                                        style={{
                                            background: mov.id === highlightedId ? "#e3f2fd" : "transparent",
                                            transition: "background 0.5s"
                                        }}
                                    >
                                        <Box
                                            position="absolute"
                                            left={12}
                                            top={8}
                                            width={18}
                                            height={18}
                                            borderRadius="50%"
                                            bgcolor={idx === 0 ? "#1976d2" : "#5d7b9d"}
                                            border="3px solid #fff"
                                            boxShadow="0 2px 4px rgba(0,0,0,0.2)"
                                        />

                                        <Paper
                                            elevation={2}
                                            onClick={(tieneAdjunto(mov) || esExpVinculado(mov)) ? () => abrirEscrito(mov) : undefined}
                                            style={{
                                                padding: 16,
                                                borderRadius: 4,
                                                border: mov.id === highlightedId ? "2px solid #1976d2" : "1px solid #e0e0e0",
                                                cursor: (tieneAdjunto(mov) || esExpVinculado(mov)) ? "pointer" : "default",
                                                transition: "all 0.2s"
                                            }}
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.transform = "translateX(4px)";
                                                e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.15)";
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.transform = "translateX(0)";
                                                e.currentTarget.style.boxShadow = "0 2px 4px rgba(0,0,0,0.1)";
                                            }}
                                        >
                                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                                <Chip
                                                    label={formatFechaHora(mov.fechaRaw || mov.fecha)}
                                                    size="small"
                                                    style={{
                                                        background: "#e3f2fd",
                                                        color: "#1976d2",
                                                        fontWeight: 600
                                                    }}
                                                />
                                                <Typography variant="subtitle2" style={{ fontWeight: 700, color: "#5d7b9d" }}>
                                                    {cap(displayTipoLabel(mov.tipo))}
                                                </Typography>
                                            </Box>

                                            <Typography variant="body1" style={{ marginBottom: 8 }}>
                                                {q ? highlightText(cap(plainText(mov.descripcion || "")), q) : cap(plainText(mov.descripcion || ""))}
                                            </Typography>

                                            <Box display="flex" justifyContent="space-between" alignItems="center" mt={2}>
                                                <Typography variant="caption" color="text.secondary">
                                                    Por: {q ? highlightText(mov.responsable, q) : mov.responsable}
                                                </Typography>

                                                <Box display="flex" gap={1}>
                                                    {esExpVinculado(mov) && (
                                                        <Tooltip title="Abrir expediente vinculado">
                                                            <IconButton size="small" onClick={(e) => { e.stopPropagation(); abrirEscrito(mov); }}>
                                                                <FolderIcon style={{ color: "#1976d2" }} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                    {tieneAdjunto(mov) && (
                                                        <Tooltip title="Ver adjunto">
                                                            <IconButton size="small" onClick={(e) => { e.stopPropagation(); abrirEscrito(mov); }}>
                                                                {(() => {
                                                                    const url = String(mov.adjunto_url || "");
                                                                    const ext = url.startsWith("vinculado:") ? (url.split(":")[2] || "").toLowerCase() : "";
                                                                    if (ext === "pdf" || url === "pdf") return <PictureAsPdfIcon style={{ color: "#d32f2f" }} />;
                                                                    if (["jpg","jpeg","png","gif"].includes(ext)) return <ImageIcon style={{ color: "#2e7d32" }} />;
                                                                    if (["doc","docx"].includes(ext)) return <DescriptionIcon style={{ color: "#1976d2" }} />;
                                                                    if (["xls","xlsx"].includes(ext)) return <DescriptionIcon style={{ color: "#2e7d32" }} />;
                                                                    return <VisibilityIcon style={{ color: "#1976d2" }} />;
                                                                })()}
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                    {mov.escrito_contenido && String(mov.escrito_contenido).trim() && (
                                                        <Tooltip title="Enviar a mi bandeja de salida (cola PJ)">
                                                            <IconButton size="small" onClick={async (e) => {
                                                                e.stopPropagation();
                                                                try {
                                                                    const r = await bandejaSalidaAgregarJWT({
                                                                        idExpediente: idExpediente,
                                                                        tipo: 'ESCRITO',
                                                                        titulo: (mov.descripcion || `Escrito ${mov.id}`).slice(0, 500),
                                                                        idMovimiento: mov.id,
                                                                        htmlContenido: mov.escrito_contenido,
                                                                        origenInfo: `Manual desde Movimientos · ${mov.tipo || ''}`,
                                                                    });
                                                                    if (r?.ok) setSnackbar({ open: true, severity: 'success', msg: 'Enviado a tu Bandeja de Salida' });
                                                                    else setSnackbar({ open: true, severity: 'error', msg: r?.mensaje || 'Error al enviar' });
                                                                } catch (err) {
                                                                    setSnackbar({ open: true, severity: 'error', msg: 'Error al enviar a la bandeja' });
                                                                }
                                                            }}>
                                                                <OutboxIcon style={{ color: '#0277bd' }} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}

                                                    {perm.update && !isTipo(mov.tipo, "ESTADO PROCESO") && !isTipo(mov.tipo, "CARTA PODER") && puedeModificarMov(mov) && (
                                                        <Tooltip title="Editar">
                                                            <IconButton size="small" onClick={(e) => { e.stopPropagation(); startEdit(mov.id); }}>
                                                                <EditIcon style={{ color: "#1976d2" }} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}

                                                    {perm.delete && puedeModificarMov(mov) && (
                                                        <Tooltip title="Eliminar">
                                                            <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleDelete(mov.id); }}>
                                                                <DeleteIcon style={{ color: "#d32f2f" }} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                </Box>
                                            </Box>
                                        </Paper>
                                    </Box>
                                ))}
                            </Box>
                        )}
                    </Box>

                    {movimientosOrdenados.length > 0 && (
                        <TablePagination
                            component="div"
                            count={movimientosOrdenados.length}
                            page={page}
                            onPageChange={(_, newPage) => setPage(newPage)}
                            rowsPerPage={rowsPerPage}
                            onRowsPerPageChange={(e) => {
                                setRowsPerPage(parseInt(e.target.value, 10));
                                setPage(0);
                            }}
                            rowsPerPageOptions={[10, 25, 50, 100]}
                            labelRowsPerPage="Filas por página:"
                            labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
                        />
                    )}
                </>
            ) : viewMode === "calendar" ? (
                // ⭐ VISTA CALENDARIO
                (<Box style={{ height: 650, padding: 16 }}>
                    {movimientosOrdenados.length === 0 ? (
                        <Typography variant="body2" color="text.secondary" align="center" style={{ padding: 40 }}>
                            No hay movimientos para mostrar en el calendario
                        </Typography>
                    ) : (
                        <Calendar
                            localizer={localizer}
                            events={eventosCalendario}
                            startAccessor="start"
                            endAccessor="end"
                            style={{ height: '100%' }}
                            messages={{
                                next: "Siguiente",
                                previous: "Anterior",
                                today: "Hoy",
                                month: "Mes",
                                week: "Semana",
                                day: "Día",
                                agenda: "Agenda",
                                date: "Fecha",
                                time: "Hora",
                                event: "Movimiento",
                                noEventsInRange: "No hay movimientos en este rango",
                                showMore: (total) => `+${total} más`
                            }}
                            views={['month', 'week', 'day', 'agenda']}
                            defaultView="month"
                            onSelectEvent={(event) => setCalendarEvent(event.resource)}
                            eventPropGetter={(event) => {
                                const mov = event.resource;
                                const tipo = displayTipoLabel(mov.tipo);

                                // Colores según tipo
                                let bgColor = '#1976d2';
                                if (isTipo(mov.tipo, "ESTADO PROCESO")) bgColor = '#f57c00';
                                else if (tipo.includes("CEDULA")) bgColor = '#9c27b0';
                                else if (tipo.includes("CARTA")) bgColor = '#00796b';
                                else if (tipo.includes("PROCESAL")) bgColor = '#c62828';

                                return {
                                    style: {
                                        backgroundColor: bgColor,
                                        borderRadius: '4px',
                                        opacity: 0.9,
                                        color: 'white',
                                        border: '0px',
                                        display: 'block',
                                        fontSize: '12px',
                                        padding: '2px 4px'
                                    }
                                };
                            }}
                        />
                    )}
                </Box>)
            ) : viewMode === "stats" ? (
                // ⭐ VISTA ESTADÍSTICAS
                (<Box p={3}>
                    <Typography variant="h5" gutterBottom style={{ fontWeight: 700, marginBottom: 24 }}>
                        Estadisticas de Movimientos
                    </Typography>
                    <Grid container spacing={3}>
                        {/* Cards de resumen - clickeables para filtrar datos dentro de stats */}
                        {[
                            { key: "total", label: "Total Movimientos", value: movimientos.length, bg: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' },
                            { key: "adjunto", label: "Con Adjunto", value: movimientos.filter(m => m.adjunto_url || (m.escrito_contenido && String(m.escrito_contenido).trim()) || hasEscrito(m.escrito)).length, bg: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' },
                            { key: "estado", label: "Cambios de Estado", value: movimientos.filter(m => isTipo(m.tipo, "ESTADO PROCESO")).length, bg: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)' },
                            { key: "responsables", label: "Responsables", value: new Set(movimientos.map(m => m.responsable)).size, bg: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)' },
                        ].map(card => (
                            <Grid key={card.key} size={{ xs: 12, sm: 6, md: 3 }}>
                                <Paper elevation={statsFilter === card.key ? 6 : 2} onClick={() => setStatsFilter(statsFilter === card.key ? null : card.key)}
                                    style={{
                                        padding: 20, borderRadius: 4, background: card.bg, cursor: 'pointer',
                                        transition: 'transform 0.2s, box-shadow 0.2s',
                                        transform: statsFilter === card.key ? 'scale(1.04)' : 'scale(1)',
                                        outline: statsFilter === card.key ? '3px solid rgba(255,255,255,0.7)' : 'none',
                                    }}
                                    onMouseEnter={e => { if (statsFilter !== card.key) e.currentTarget.style.transform = 'scale(1.03)'; }}
                                    onMouseLeave={e => { if (statsFilter !== card.key) e.currentTarget.style.transform = 'scale(1)'; }}
                                >
                                    <Typography variant="h3" style={{ color: '#fff', fontWeight: 700 }}>{card.value}</Typography>
                                    <Typography variant="body2" style={{ color: 'rgba(255,255,255,0.9)' }}>{card.label}</Typography>
                                </Paper>
                            </Grid>
                        ))}

                        {/* Detalle expandible al seleccionar una card */}
                        {statsFilter && (
                            <Grid size={{ xs: 12 }}>
                                <Paper elevation={1} style={{ padding: 20, borderRadius: 4, background: '#fafafa' }}>
                                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                                        <Typography variant="h6" style={{ fontWeight: 600 }}>
                                            {statsFilter === "total" && "Todos los Movimientos"}
                                            {statsFilter === "adjunto" && "Movimientos con Adjunto"}
                                            {statsFilter === "estado" && "Cambios de Estado Procesal"}
                                            {statsFilter === "responsables" && "Movimientos por Responsable"}
                                        </Typography>
                                        <Button size="small" onClick={() => setStatsFilter(null)}>Cerrar</Button>
                                    </Box>
                                    {statsFilter === "responsables" ? (
                                        // Responsables: desglose por responsable
                                        (() => {
                                            const porResp = {};
                                            movimientos.forEach(m => { const r = m.responsable || "Sin responsable"; porResp[r] = (porResp[r] || 0) + 1; });
                                            const sorted = Object.entries(porResp).sort((a, b) => b[1] - a[1]);
                                            const max = Math.max(...sorted.map(([, c]) => c));
                                            return sorted.map(([resp, count]) => (
                                                <Box key={resp} mb={1}>
                                                    <Box display="flex" justifyContent="space-between" mb={0.5}>
                                                        <Typography variant="body2" style={{ fontSize: 13 }}>{resp}</Typography>
                                                        <Typography variant="body2" style={{ fontWeight: 700 }}>{count}</Typography>
                                                    </Box>
                                                    <Box style={{ height: 6, background: '#e0e0e0', borderRadius: 4, overflow: 'hidden' }}>
                                                        <Box style={{ height: '100%', width: `${(count / max) * 100}%`, background: 'linear-gradient(90deg, #43e97b, #38f9d7)', transition: 'width 0.3s' }} />
                                                    </Box>
                                                </Box>
                                            ));
                                        })()
                                    ) : (
                                        // Total, adjunto, estado: listado de movimientos
                                        (() => {
                                            let lista = movimientos;
                                            if (statsFilter === "adjunto") lista = lista.filter(m => m.adjunto_url || (m.escrito_contenido && String(m.escrito_contenido).trim()) || hasEscrito(m.escrito));
                                            if (statsFilter === "estado") lista = lista.filter(m => isTipo(m.tipo, "ESTADO PROCESO"));
                                            return (
                                                <Box style={{ maxHeight: 300, overflow: 'auto' }}>
                                                    <Table size="small">
                                                        <TableHead>
                                                            <TableRow>
                                                                <TableCell style={{ fontWeight: 700 }}>Fecha</TableCell>
                                                                <TableCell style={{ fontWeight: 700 }}>Tipo</TableCell>
                                                                <TableCell style={{ fontWeight: 700 }}>Descripcion</TableCell>
                                                                <TableCell style={{ fontWeight: 700 }}>Responsable</TableCell>
                                                            </TableRow>
                                                        </TableHead>
                                                        <TableBody>
                                                            {lista.slice(0, 50).map(m => (
                                                                <TableRow key={m.id} hover style={{ cursor: (tieneAdjunto(m) || esExpVinculado(m)) ? 'pointer' : 'default' }}
                                                                    onClick={(tieneAdjunto(m) || esExpVinculado(m)) ? () => abrirEscrito(m) : undefined}>
                                                                    <TableCell style={{ fontSize: 12 }}>{formatFechaHora(m.fechaRaw || m.fecha)}</TableCell>
                                                                    <TableCell style={{ fontSize: 12 }}>{cap(displayTipoLabel(m.tipo))}</TableCell>
                                                                    <TableCell style={{ fontSize: 12 }}>{cap(plainText(m.descripcion || "")).substring(0, 60)}</TableCell>
                                                                    <TableCell style={{ fontSize: 12 }}>{m.responsable}</TableCell>
                                                                </TableRow>
                                                            ))}
                                                        </TableBody>
                                                    </Table>
                                                    {lista.length > 50 && <Typography variant="caption" color="text.secondary" style={{ padding: 8, display: 'block' }}>Mostrando 50 de {lista.length}</Typography>}
                                                </Box>
                                            );
                                        })()
                                    )}
                                </Paper>
                            </Grid>
                        )}

                        {/* Gráfico: Movimientos por tipo */}
                        <Grid size={{ xs: 12, md: 6 }}>
                            <Paper elevation={2} style={{ padding: 20, borderRadius: 4 }}>
                                <Typography variant="h6" gutterBottom style={{ fontWeight: 600 }}>
                                    Movimientos por Tipo
                                </Typography>
                                <Box mt={2}>
                                    {(() => {
                                        const porTipo = {};
                                        movimientos.forEach(m => {
                                            const tipo = displayTipoLabel(m.tipo);
                                            porTipo[tipo] = (porTipo[tipo] || 0) + 1;
                                        });
                                        const sorted = Object.entries(porTipo).sort((a, b) => b[1] - a[1]).slice(0, 10);
                                        const max = Math.max(...sorted.map(([, count]) => count));

                                        return sorted.map(([tipo, count]) => (
                                            <Box key={tipo} mb={1.5}>
                                                <Box display="flex" justifyContent="space-between" mb={0.5}>
                                                    <Typography variant="body2" style={{ fontSize: 13 }}>{tipo}</Typography>
                                                    <Typography variant="body2" style={{ fontWeight: 700 }}>{count}</Typography>
                                                </Box>
                                                <Box style={{ height: 8, background: '#e0e0e0', borderRadius: 4, overflow: 'hidden' }}>
                                                    <Box style={{ height: '100%', width: `${(count / max) * 100}%`, background: 'linear-gradient(90deg, #1976d2, #42a5f5)', transition: 'width 0.3s' }} />
                                                </Box>
                                            </Box>
                                        ));
                                    })()}
                                </Box>
                            </Paper>
                        </Grid>

                        {/* Timeline de actividad */}
                        <Grid size={{ xs: 12, md: 6 }}>
                            <Paper elevation={2} style={{ padding: 20, borderRadius: 4 }}>
                                <Typography variant="h6" gutterBottom style={{ fontWeight: 600 }}>
                                    Actividad por Mes
                                </Typography>
                                <Box mt={2}>
                                    {(() => {
                                        const porMes = {};
                                        movimientos.forEach(m => {
                                            const fecha = parseFechaAny(m.fechaRaw || m.fecha);
                                            if (fecha) {
                                                const mes = `${fecha.getFullYear()}-${String(fecha.getMonth() + 1).padStart(2, '0')}`;
                                                porMes[mes] = (porMes[mes] || 0) + 1;
                                            }
                                        });
                                        const sorted = Object.entries(porMes).sort((a, b) => b[0].localeCompare(a[0])).slice(0, 12);
                                        const max = Math.max(...sorted.map(([, count]) => count));

                                        return sorted.map(([mes, count]) => {
                                            const [year, month] = mes.split('-');
                                            const monthName = new Date(year, parseInt(month) - 1).toLocaleDateString('es-AR', { month: 'short', year: 'numeric' });

                                            return (
                                                <Box key={mes} mb={1.5}>
                                                    <Box display="flex" justifyContent="space-between" mb={0.5}>
                                                        <Typography variant="body2" style={{ fontSize: 13, textTransform: 'capitalize' }}>
                                                            {monthName}
                                                        </Typography>
                                                        <Typography variant="body2" style={{ fontWeight: 700 }}>{count}</Typography>
                                                    </Box>
                                                    <Box style={{ height: 8, background: '#e0e0e0', borderRadius: 4, overflow: 'hidden' }}>
                                                        <Box style={{ height: '100%', width: `${(count / max) * 100}%`, background: 'linear-gradient(90deg, #f57c00, #ff9800)', transition: 'width 0.3s' }} />
                                                    </Box>
                                                </Box>
                                            );
                                        });
                                    })()}
                                </Box>
                            </Paper>
                        </Grid>
                    </Grid>
                </Box>)
            ) : null}
            {/* Dialog detalle evento calendario */}
            <Dialog open={!!calendarEvent} onClose={() => setCalendarEvent(null)} maxWidth="sm" fullWidth>
                <DialogTitle style={{ background: "#5d7b9d", color: "#fff", padding: "12px 20px" }}>
                    <span style={{ fontWeight: 700 }}>Detalle del Movimiento</span>
                </DialogTitle>
                {calendarEvent && (
                    <DialogContent style={{ padding: "20px 24px" }}>
                        <Box display="flex" flexDirection="column" gap={2} mt={1}>
                            <Box display="flex" alignItems="center" gap={1}>
                                <EventIcon color="action" />
                                <Typography variant="body1"><b>Fecha:</b> {formatFechaHora(calendarEvent.fechaRaw || calendarEvent.fecha)}</Typography>
                            </Box>
                            <Box display="flex" alignItems="center" gap={1}>
                                <BookmarkIcon color="action" />
                                <Typography variant="body1"><b>Tipo:</b> {cap(displayTipoLabel(calendarEvent.tipo))}</Typography>
                            </Box>
                            <Box display="flex" alignItems="center" gap={1}>
                                <DescriptionIcon color="action" />
                                <Typography variant="body1"><b>Descripcion:</b> {cap(plainText(calendarEvent.descripcion || "-"))}</Typography>
                            </Box>
                            <Box display="flex" alignItems="center" gap={1}>
                                <PersonIcon color="action" />
                                <Typography variant="body1"><b>Responsable:</b> {calendarEvent.responsable || "-"}</Typography>
                            </Box>
                            {(tieneAdjunto(calendarEvent) || esExpVinculado(calendarEvent)) && (
                                <Box display="flex" alignItems="center" gap={1}>
                                    <AttachFileIcon color="action" />
                                    <Typography variant="body1"><b>Adjunto:</b></Typography>
                                    {(() => {
                                        const mov = calendarEvent;
                                        if (esExpVinculado(mov)) return <Chip size="small" icon={<FolderIcon />} label="Expediente" color="primary" variant="filled" />;
                                        const url = String(mov.adjunto_url || "");
                                        if (url.startsWith("vinculado:")) {
                                            const ext = (url.split(":")[2] || "").toLowerCase();
                                            if (ext === "pdf") return <Chip size="small" icon={<PictureAsPdfIcon />} label="PDF" color="error" variant="filled" />;
                                            if (["jpg","jpeg","png","gif"].includes(ext)) return <Chip size="small" icon={<ImageIcon />} label="Imagen" color="success" variant="filled" />;
                                            if (["doc","docx"].includes(ext)) return <Chip size="small" icon={<DescriptionIcon />} label="Word" color="primary" variant="filled" />;
                                            if (["xls","xlsx"].includes(ext)) return <Chip size="small" icon={<DescriptionIcon />} label="Excel" color="success" variant="filled" />;
                                        }
                                        if (url === "pdf") return <Chip size="small" icon={<PictureAsPdfIcon />} label="PDF" color="error" variant="filled" />;
                                        if (mov.escrito_contenido || hasEscrito(mov.escrito)) return <Chip size="small" icon={<AssignmentIcon />} label="Escrito" color="info" variant="filled" />;
                                        return <Chip size="small" icon={<AttachFileIcon />} label="Adjunto" variant="filled" />;
                                    })()}
                                </Box>
                            )}
                        </Box>
                    </DialogContent>
                )}
                <DialogActions style={{ padding: "12px 20px", gap: 8 }}>
                    {calendarEvent && (tieneAdjunto(calendarEvent) || esExpVinculado(calendarEvent)) && (
                        <Button variant="outlined" startIcon={<VisibilityIcon />} onClick={() => { abrirEscrito(calendarEvent); setCalendarEvent(null); }}>
                            Ver adjunto
                        </Button>
                    )}
                    {calendarEvent && perm.update && !isTipo(calendarEvent.tipo, "ESTADO PROCESO") && !isTipo(calendarEvent.tipo, "CARTA PODER") && puedeModificarMov(calendarEvent) && (
                        <Button variant="outlined" color="primary" startIcon={<EditIcon />} onClick={() => { startEdit(calendarEvent.id); setCalendarEvent(null); }}>
                            Editar
                        </Button>
                    )}
                    <Button variant="contained" onClick={() => setCalendarEvent(null)}>Cerrar</Button>
                </DialogActions>
            </Dialog>
            {/* Diálogo alta/edición */}
            <Dialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                maxWidth="md"
                fullWidth
                keepMounted={false}
                disableEnforceFocus
                disableAutoFocus
                disableRestoreFocus
                PaperProps={{ style: { overflow: 'visible' } }}
            >
                <DialogTitle style={{ background: "#5d7b9d", padding: 18, textAlign: "center" }}>
                    <span style={{ fontWeight: 700, fontSize: 22, color: "#fff" }}>
                        {isNew ? "Nuevo Movimiento" : "Editar Movimiento"}
                    </span>
                </DialogTitle>
                <DialogContent style={{ background: "#fafafa", padding: "26px 30px" }}>
                    <Grid container spacing={2}>
                        {/* Fecha */}
                        <Grid size={{ xs: 12, sm: 4 }}>
                            <TextField
                                type="date"
                                label="Fecha *"
                                variant="outlined"
                                size="small"
                                value={editData.fecha || ""}
                                onChange={e => setEditData(d => ({ ...d, fecha: e.target.value }))}
                                InputLabelProps={{ shrink: true }}
                                fullWidth
                            />
                        </Grid>

                        {/* Tipo */}
                        <Grid size={{ xs: 12, sm: 4 }}>
                            <Autocomplete
                                options={opcionesTipo}
                                getOptionLabel={option => {
                                    if (option.isDivider) return "";
                                    return cap(option.labelUI || option.tipo || "");
                                }}
                                getOptionDisabled={option => option.isDivider === true}
                                renderOption={(props, option) => {
                                    const { key, ...rest } = props;
                                    const uKey = option.tipo || key;
                                    if (option.isDivider) {
                                        return (
                                            <li key={uKey} {...rest} style={{ padding: 0 }}>
                                                <Divider
                                                    style={{ backgroundColor: '#5d7b9d', margin: '2px 0', width: '100%' }}
                                                />
                                            </li>
                                        );
                                    }

                                    // Seleccionar icono según tipo
                                    let Icon = DescriptionIcon;
                                    const k = keyify(option.tipo);

                                    if (k === keyify("CARTA PODER")) Icon = DescriptionIcon;
                                    else if (k === keyify("CEDULA") || k === keyify("CÉDULA")) Icon = AssignmentIcon;
                                    else if (k === keyify("REFERENCIA")) Icon = BookmarkIcon;
                                    else if (k === keyify("PROCESAL")) Icon = GavelIcon;
                                    else if (k === keyify("EXTRA PROCESAL")) Icon = AttachFileIcon;
                                    else if (k === keyify("MIS ESCRITOS")) Icon = PersonIcon;
                                    else if (k === keyify("OTRO MOV")) Icon = FolderIcon;
                                    else if (k === keyify("RECIBOS")) Icon = ReceiptIcon;
                                    else if (k === keyify("OFIGEN") || k === keyify("OFICINA DE PAGOS")) Icon = ReceiptIcon;
                                    else if (k === keyify("EMBARGO")) Icon = AccountBalanceIcon;

                                    return (
                                        <li key={uKey} {...rest}>
                                            <Box style={{ display: "flex", alignItems: "center", gap: 12 }}>
                                                <Icon style={{ fontSize: 20, color: "#1976d2", opacity: 0.8 }} />
                                                <span>{cap(option.labelUI || option.tipo || "")}</span>
                                            </Box>
                                        </li>
                                    );
                                }}
                                value={(() => {
                                    if (!editData.tipo) return null;
                                    const found = opcionesTipo.find(t => !t.isDivider && keyify(t.tipo) === keyify(editData.tipo));
                                    return found || null;
                                })()}
                                onChange={(_, newValue) => {
                                    if (!newValue || newValue.isDivider) return;

                                    const nuevoTipo = newValue.tipo;

                                    // ⭐ Si selecciona "CARTA PODER", ir directo a generar (envío al fiscal)
                                    if (isCartaPoder(nuevoTipo)) {
                                        const caratulaAuto = expedienteVarsRef.current?.CARATULA || expedienteVarsRef.current?.Caratula || '';
                                        setEditData(d => ({ ...d, tipo: "GENERADOR DE CARTA PODER", escrito: "", descripcion: caratulaAuto }));
                                        setEscritoContenido("");
                                        setRedactarEnBlanco(false);
                                        setAdjuntoNombre("");
                                        setAdjuntoBase64("");
                                        setEditorKey(prev => prev + 1);
                                        return;
                                    }

                                    setEditData(d => {
                                        const saliendoDeCP = (isCartaPoder(d.tipo) || isGenCartaPoder(d.tipo)) &&
                                            !isCartaPoder(nuevoTipo) && !isGenCartaPoder(nuevoTipo);
                                        let nuevaDesc = d.descripcion || "";
                                        if (saliendoDeCP && descAutoFromCP) nuevaDesc = "";
                                        return { ...d, tipo: nuevoTipo, id_tipo_movimiento: newValue.id || null, descripcion: nuevaDesc, escrito: "" };
                                    });
                                    setDescAutoFromCP(false);
                                    descAutoValueRef.current = "";
                                    setEscritoContenido("");
                                    setRedactarEnBlanco(false);
                                    setAdjuntoNombre("");
                                    setAdjuntoBase64("");

                                    // Forzar re-mount del editor cuando cambia el tipo
                                    setEditorKey(prev => prev + 1);
                                }}
                                renderInput={params => <TextField {...params} label="Tipo *" variant="outlined" size="small" fullWidth />}
                                disabled={!isNew}
                            />
                        </Grid>
                        
                        {/* ⭐ Selector de Escrito: Solo si el tipo lo requiere */}
                        {editData.tipo && tipoReqEscrito(editData.tipo) && (
                            <Grid size={{ xs: 12, sm: 4 }}>
                                <Autocomplete
                                    options={escritosFiltrados}
                                    getOptionLabel={option => option.escrito || ""}
                                    isOptionEqualToValue={(option, value) => option.id === value.id || option.escrito === value.escrito}
                                    renderOption={(props, option) => {
                                        const { key, ...rest } = props;
                                        const esPrivado = (option?.scope || "").toUpperCase() === "PRIVATE" && option?.ownerUsuario === usuario;
                                        return (
                                            <li key={option.id != null ? option.id : key} {...rest}>
                                                <Box style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                                    {esPrivado && (
                                                        <Chip
                                                            label="Mío"
                                                            size="small"
                                                            style={{
                                                                background: "#e3f2fd",
                                                                color: "#1976d2",
                                                                fontWeight: 600,
                                                                fontSize: 10,
                                                                height: 18
                                                            }}
                                                        />
                                                    )}
                                                    <span>{option.escrito}</span>
                                                </Box>
                                            </li>
                                        );
                                    }}
                                    value={escritosFiltrados.find(e => e.escrito === (editData.escrito || "")) || null}
                                    onChange={async (_, newValue) => {

                                        setEditData(d => ({ ...d, escrito: newValue ? newValue.escrito : "", idEscrito: newValue ? newValue.id : null }));
                                        // Elegir una plantilla del combo sale del modo "redacción en blanco".
                                        setRedactarEnBlanco(false);

                                        if (newValue) {
                                            try {
                                                const html = await obtenerEscritoGenerado(newValue.id, idExpediente);
                                                // Guardar el HTML "template" antes de reemplazar — sirve para
                                                // refrescar el editor en vivo cuando el usuario tipea descripción
                                                // o fojas (sin perder el lugar de las variables).
                                                escritoTemplateRef.current = html || '';

                                                // Cargar datos del expediente y reemplazar variables
                                                const rawExp = await fetchExpedienteVarsFlexible(idExpediente);
                                                // ⭐ Usar la ref actualizada para tener los datos más recientes
                                                const movData = { ...editDataRef.current, escrito: newValue.escrito, usuario };
                                                expedienteVarsRef.current = buildGlossaryFromExpediente(rawExp, movData);

                                                const res = replaceVarsInHtml(html || '', expedienteVarsRef.current);

                                                // Actualizar el contenido
                                                const nuevoHtml = res.html || '';
                                                setEscritoContenido(nuevoHtml);
                                                escritoContenidoRef.current = nuevoHtml;
                                                missingVarsRef.current = res.missing || [];
                                                setMissingVars(res.missing || []);

                                                // Si el editor ya está listo, actualizar directamente
                                                if (editorApiRef.current?.setContent) {
                                                    setTimeout(() => {
                                                        editorApiRef.current.setContent(nuevoHtml);
                                                    }, 100);
                                                } else {
                                                    // Si no está listo, forzar re-mount para que cargue el contenido
                                                    setTimeout(() => {
                                                        setEditorKey(prev => prev + 1);
                                                    }, 0);
                                                }
                                            } catch (error) {
                                                console.error('Error cargando escrito:', error);
                                                setEscritoContenido("");
                                                escritoContenidoRef.current = "";
                                                if (editorApiRef.current?.setContent) {
                                                    editorApiRef.current.setContent("");
                                                }
                                                setSnackbar({ open: true, msg: "No se pudo cargar el escrito.", severity: "error" });
                                            }
                                        } else {
                                            setEscritoContenido("");
                                            escritoContenidoRef.current = "";
                                            if (editorApiRef.current?.setContent) {
                                                editorApiRef.current.setContent("");
                                            }
                                        }
                                    }}
                                    renderInput={params => <TextField {...params} label="Escrito" variant="outlined" size="small" fullWidth />}
                                />
                            </Grid>
                        )}

                        {/* #2 — Redactar el escrito desde cero, sin partir de una plantilla del combo */}
                        {editData.tipo && tipoReqEscrito(editData.tipo) && !editData.escrito && !redactarEnBlanco && (
                            <Grid size={{ xs: 12, sm: 4 }} style={{ display: 'flex', alignItems: 'center' }}>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    startIcon={<DescriptionIcon />}
                                    onClick={() => {
                                        setEditData(d => ({ ...d, escrito: "", idEscrito: null }));
                                        escritoTemplateRef.current = '';
                                        setEscritoContenido("");
                                        escritoContenidoRef.current = "";
                                        setRedactarEnBlanco(true);
                                        setEditorKey(prev => prev + 1);
                                    }}
                                    style={{ height: 40 }}
                                >
                                    Redactar en blanco
                                </Button>
                            </Grid>
                        )}

                        {/* === EMBARGO: campos específicos === */}
                        {isEmbargo(editData.tipo) && (
                            <>
                                <Grid size={{ xs: 12, sm: 4 }}>
                                    <TextField
                                        select
                                        label="Estado del Embargo *"
                                        variant="outlined"
                                        size="small"
                                        value={editData.embargoEstado || ""}
                                        onChange={e => { setEditData(d => ({ ...d, embargoEstado: e.target.value })); setEmbargoValidado(false); }}
                                        fullWidth
                                        error={embargoValidado && !editData.embargoEstado}
                                        helperText={embargoValidado && !editData.embargoEstado ? "Seleccioná el estado" : ""}
                                    >
                                        {EMBARGO_ESTADOS.map(est => (
                                            <MenuItem key={est} value={est}>{est}</MenuItem>
                                        ))}
                                    </TextField>
                                </Grid>
                                <Grid size={{ xs: 12, sm: 4 }}>
                                    <TextField
                                        type="date"
                                        label="Fecha del Embargo *"
                                        variant="outlined"
                                        size="small"
                                        value={editData.embargoFecha || ""}
                                        onChange={e => { setEditData(d => ({ ...d, embargoFecha: e.target.value })); setEmbargoValidado(false); }}
                                        InputLabelProps={{ shrink: true }}
                                        fullWidth
                                        error={embargoValidado && !editData.embargoFecha}
                                        helperText={embargoValidado && !editData.embargoFecha ? "Ingresá la fecha" : ""}
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, sm: 4 }}>
                                    <TextField
                                        label="Monto *"
                                        variant="outlined"
                                        size="small"
                                        type="number"
                                        value={editData.embargoMonto ?? ""}
                                        onChange={e => { setEditData(d => ({ ...d, embargoMonto: e.target.value === "" ? null : parseFloat(e.target.value) })); setEmbargoValidado(false); }}
                                        fullWidth
                                        inputProps={{ step: "0.01", min: "0" }}
                                        error={embargoValidado && (editData.embargoMonto == null || editData.embargoMonto === '')}
                                        helperText={embargoValidado && (editData.embargoMonto == null || editData.embargoMonto === '') ? "Ingresá el monto" : ""}
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, sm: 4 }}>
                                    <TextField
                                        label="Folio Depósito Judicial"
                                        variant="outlined"
                                        size="small"
                                        value={editData.embargoFolioDepositoJudicial || ""}
                                        onChange={e => setEditData(d => ({ ...d, embargoFolioDepositoJudicial: e.target.value }))}
                                        fullWidth
                                        inputProps={{ maxLength: 50 }}
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, sm: 4 }}>
                                    <TextField
                                        label="Banco Embargante"
                                        variant="outlined"
                                        size="small"
                                        value={editData.embargoBancoEmbargante || ""}
                                        onChange={e => setEditData(d => ({ ...d, embargoBancoEmbargante: e.target.value.toUpperCase() }))}
                                        fullWidth
                                        inputProps={{ maxLength: 50 }}
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, sm: 4 }}>
                                    <TextField
                                        label="Nº Transacción"
                                        variant="outlined"
                                        size="small"
                                        value={editData.embargoNumTransaccion || ""}
                                        onChange={e => setEditData(d => ({ ...d, embargoNumTransaccion: e.target.value }))}
                                        fullWidth
                                        inputProps={{ maxLength: 50 }}
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, sm: 4 }}>
                                    <TextField
                                        label="Cheque Judicial"
                                        variant="outlined"
                                        size="small"
                                        value={editData.embargoChequeJudicial || ""}
                                        onChange={e => setEditData(d => ({ ...d, embargoChequeJudicial: e.target.value }))}
                                        fullWidth
                                        inputProps={{ maxLength: 50 }}
                                    />
                                </Grid>
                            </>
                        )}

                        {/* Descripción (oculta en Carta Poder y Embargo) */}
                        {!isGenCartaPoder(editData.tipo) && !isEmbargo(editData.tipo) && (
                        <Grid size={tipoExpediente !== "judicial" ? {xs: 12, sm: 9} : 12}>
                            {/* Autocomplete con historial local (últimas 50 descripciones del usuario
                                en este equipo). freeSolo permite tipear libre, las opciones sugieren
                                lo ya escrito antes. Persiste en localStorage SIGIF_MOVS_DESCRIPCIONES. */}
                            <Autocomplete
                                freeSolo
                                options={descripcionesHistorial}
                                value={plainText(editData.descripcion || "")}
                                onInputChange={(e, v) => {
                                    setEditData(d => ({ ...d, descripcion: v }));
                                    if (descAutoFromCP && v !== descAutoValueRef.current) setDescAutoFromCP(false);
                                }}
                                filterOptions={(opts, state) => {
                                    const q = (state.inputValue || '').toLowerCase().trim();
                                    if (!q) return opts.slice(0, 20);
                                    return opts.filter(o => o.toLowerCase().includes(q)).slice(0, 20);
                                }}
                                renderInput={(params) => (
                                    <TextField {...params}
                                        label="Descripción *"
                                        variant="outlined"
                                        size="small"
                                        fullWidth
                                    />
                                )}
                            />
                        </Grid>
                        )}
                        {/* Fojas - solo para expedientes administrativos (no para embargo) */}
                        {tipoExpediente !== "judicial" && !isEmbargo(editData.tipo) && (
                            <Grid size={{xs: 12, sm: 3}}>
                                <TextField
                                    label="Fojas"
                                    variant="outlined"
                                    size="small"
                                    value={editData.fojas || ""}
                                    onChange={e => {
                                        const v = e.target.value.slice(0, 4).toUpperCase();
                                        setEditData(d => ({ ...d, fojas: v }));
                                    }}
                                    inputProps={{ maxLength: 4 }}
                                    fullWidth
                                />
                            </Grid>
                        )}

                        {/* ⭐ Adjunto PDF: Solo si el tipo lo requiere */}
                        {editData.tipo && tipoReqPdf(editData.tipo) && (
                            <Grid size={12}>
                                <div style={{ marginBottom: 6 }}><b>Adjunto PDF *</b></div>
                                <input
                                    type="file"
                                    accept="application/pdf"
                                    onChange={(e) => {
                                        const f = e.target.files && e.target.files[0];
                                        if (!f) {
                                            setAdjuntoNombre("");
                                            setAdjuntoBase64("");
                                            return;
                                        }
                                        setAdjuntoNombre(f.name);
                                        if (isCartaPoder(editData.tipo)) {
                                            const baseName = f.name.replace(/\.[^.]+$/, "");
                                            setEditData(d => ({ ...d, descripcion: baseName }));
                                            setDescAutoFromCP(true);
                                            descAutoValueRef.current = baseName;
                                        }
                                        const reader = new FileReader();
                                        reader.onload = () => {
                                            const base64 = String(reader.result || "").split(",")[1] || "";
                                            setAdjuntoBase64(base64);
                                        };
                                        reader.readAsDataURL(f);
                                    }}
                                />
                                {adjuntoNombre && <div style={{ marginTop: 6 }}><Chip label={adjuntoNombre} size="small" /></div>}
                            </Grid>
                        )}

                        {/* ⭐ Editor TinyMCE: con escrito elegido, redacción en blanco, o contenido ya guardado */}
                        {dialogOpen && tipoReqEscrito(editData.tipo) && (editData.escrito || redactarEnBlanco || String(escritoContenido || '').trim()) && (
                            <Grid size={12}>
                                <Box display="flex" alignItems="center" justifyContent="space-between" style={{ marginBottom: 8 }}>
                                    <div style={{ fontWeight: 600 }}>
                                        Texto del escrito
                                    </div>
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        startIcon={<SaveIcon />}
                                        disabled={!String(escritoContenido || '').trim()}
                                        onClick={() => setGuardarDocDialog({
                                            open: true,
                                            nombre: String(editData.escrito || editData.descripcion || '').trim(),
                                            saving: false,
                                        })}
                                    >
                                        Guardar como documento
                                    </Button>
                                </Box>

                                {/* Aviso fijo de datos faltantes. Antes esto era solo un
                                    snackbar que se iba a los 6 segundos y listaba nombres
                                    de variables ({DEMANDADO_NN}), así que el escrito con
                                    llaves sin reemplazar parecía una falla del sistema en
                                    vez de un expediente incompleto. */}
                                {describirFaltantes(missingVars).length > 0 && (
                                    <Alert severity="warning" sx={{ mb: 1.5, borderRadius: '4px' }}>
                                        <AlertTitle sx={{ fontWeight: 600, mb: 0.5 }}>
                                            Faltan datos del expediente
                                        </AlertTitle>
                                        El escrito quedó con los espacios sin completar porque el
                                        expediente no tiene cargado:
                                        <Box component="ul" sx={{ my: 0.5, pl: 2.5 }}>
                                            {describirFaltantes(missingVars).map(({ pantalla, datos }) => (
                                                <li key={pantalla}>
                                                    <b>{datos.join(', ')}</b>
                                                    {pantalla ? <> — se carga en <b>{pantalla}</b></> : null}
                                                </li>
                                            ))}
                                        </Box>
                                        Completalos y volvé a elegir el escrito para regenerarlo, o
                                        escribí los datos a mano en el texto de abajo.
                                    </Alert>
                                )}

                                <TinyEditor
                                    key={editorKey}
                                    id="tinymce-escrito"
                                    initialHtml={escritoContenido}
                                    onChange={handleEditorChange}
                                    onReady={handleEditorReady}
                                />
                            </Grid>
                        )}
                    </Grid>
                </DialogContent>
                <DialogActions style={{ background: "#f5f5f5", padding: "16px 28px" }}>
                    <Button onClick={() => setDialogOpen(false)}
                    color="secondary" variant="outlined" startIcon={<CancelIcon />} style={{ fontWeight: 500 }}>
                        Cancelar
                    </Button>
                    <Button
                        onClick={saveEdit}
                        color="primary"
                        variant="contained"
                        startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
                        disabled={
                            saving ||
                            !editData.fecha ||
                            !editData.tipo ||
                            (!isGenCartaPoder(editData.tipo) && !isEmbargo(editData.tipo) && !String(editData.descripcion || "").trim()) ||
                            (isEmbargo(editData.tipo) && !editData.embargoEstado) ||
                            (tipoReqEscrito(editData.tipo) && !editData.escrito && !String(escritoContenido || '').trim()) ||
                            (tipoReqPdf(editData.tipo) && !adjuntoBase64) ||
                            (isNew && !perm.insert) || (!isNew && !perm.update)
                        }
                        style={{ fontWeight: 700, ...(isGenCartaPoder(editData.tipo) ? { backgroundColor: '#2e7d32' } : {}) }}
                    >
                        {isGenCartaPoder(editData.tipo) ? 'Enviar al Fiscal para firma' : 'Guardar'}
                    </Button>
                    {!isNew && (
                        <Button
                            onClick={async () => {
                                // Forzar “nuevo”
                                const prevIsNew = isNew;
                                setIsNew(true);
                                try { await saveEdit(); } finally { setIsNew(prevIsNew); }
                            }}
                            variant="outlined"
                        >
                            Guardar como nuevo
                        </Button>
                    )}
                </DialogActions>
            </Dialog>
            {/* #2 — Guardar lo redactado como documento reutilizable */}
            <Dialog
                open={guardarDocDialog.open}
                onClose={() => !guardarDocDialog.saving && setGuardarDocDialog(d => ({ ...d, open: false }))}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle style={{ background: "#5d7b9d", textAlign: "center", fontWeight: 700, color: "#fff" }}>
                    Guardar como documento
                </DialogTitle>
                <DialogContent style={{ padding: "22px 24px" }}>
                    <Typography variant="body2" color="text.secondary" style={{ marginBottom: 14 }}>
                        El escrito redactado se guardará como documento privado en "Mis Escritos" y vas a poder reutilizarlo en otros movimientos.
                    </Typography>
                    <TextField
                        autoFocus
                        label="Nombre del documento *"
                        variant="outlined"
                        size="small"
                        fullWidth
                        value={guardarDocDialog.nombre}
                        onChange={e => setGuardarDocDialog(d => ({ ...d, nombre: e.target.value }))}
                        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); guardarComoDocumento(); } }}
                    />
                </DialogContent>
                <DialogActions style={{ padding: "12px 20px" }}>
                    <Button
                        onClick={() => setGuardarDocDialog(d => ({ ...d, open: false }))}
                        color="secondary"
                        variant="outlined"
                        disabled={guardarDocDialog.saving}
                    >
                        Cancelar
                    </Button>
                    <Button
                        onClick={guardarComoDocumento}
                        color="primary"
                        variant="contained"
                        disabled={guardarDocDialog.saving || !String(guardarDocDialog.nombre || '').trim()}
                        startIcon={guardarDocDialog.saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                    >
                        {guardarDocDialog.saving ? "Guardando..." : "Guardar"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Diálogo eliminar */}
            <Dialog open={dialogDeleteOpen} onClose={() => setDialogDeleteOpen(false)}>
                <DialogTitle style={{ background: "#5d7b9d", textAlign: "center", fontWeight: 700, color: "#fff" }}>
                    ¿Eliminar movimiento?
                </DialogTitle>
                <DialogContent style={{ fontSize: 15, padding: "18px 24px" }}>
                    <Box mb={1}>Vas a eliminar el siguiente movimiento:</Box>
                    <Box bgcolor="#f9f9f9" p={2} borderRadius="4px" mb={1}>
                        <Grid container spacing={1}>
                            <Grid size={{ xs: 12, sm: 4 }}><b>Fecha:</b> {deleteInfo?.fecha || "-"}</Grid>
                            <Grid size={{ xs: 12, sm: 4 }}><b>Tipo:</b> {deleteInfo?.tipo || "-"}</Grid>
                            <Grid size={{ xs: 12, sm: 4 }}><b>Responsable:</b> {deleteInfo?.responsable || "-"}</Grid>
                            <Grid size={12}><b>Descripción:</b><div style={{ whiteSpace: "pre-wrap" }}>{deleteInfo?.resumen || "-"}</div></Grid>
                        </Grid>
                    </Box>
                    <Box color="#b00020">Esta acción no se puede deshacer. Se registrará en el historial de cambios.</Box>
                </DialogContent>
                <DialogActions style={{ background: "#f5f5f5", padding: "12px 22px" }}>
                    <Button onClick={() => setDialogDeleteOpen(false)} color="primary" variant="outlined">Cancelar</Button>
                    <Button onClick={confirmDelete} color="secondary" variant="contained" style={{ fontWeight: 700 }}>Eliminar</Button>
                </DialogActions>
            </Dialog>

            {/* Diálogo: nuevo movimiento "Repositorio" (link externo) */}
            <Dialog open={repoDlg.open} onClose={() => !repoDlg.saving && setRepoDlg(p => ({ ...p, open: false }))} maxWidth="sm" fullWidth>
                <DialogTitle style={{ background: "#1769aa", fontWeight: 700, color: "#fff", display: "flex", alignItems: "center", gap: 8 }}>
                    <LinkIcon /> Movimiento — Repositorio
                </DialogTitle>
                <DialogContent style={{ padding: "20px 24px" }}>
                    <Box style={{ display: "flex", flexDirection: "column", gap: 16, marginTop: 8 }}>
                        <TextField
                            label="Descripción"
                            value={repoDlg.descripcion}
                            onChange={e => setRepoDlg(p => ({ ...p, descripcion: e.target.value }))}
                            placeholder="Ej: Repositorio del Poder Judicial"
                            fullWidth size="small"
                        />
                        <TextField
                            label="URL del repositorio"
                            value={repoDlg.url}
                            onChange={e => setRepoDlg(p => ({ ...p, url: e.target.value }))}
                            placeholder="https://..."
                            fullWidth size="small" autoFocus
                            helperText="Debe empezar con http:// o https://. Se abrirá en una pestaña nueva."
                        />
                    </Box>
                </DialogContent>
                <DialogActions style={{ background: "#f5f5f5", padding: "12px 22px" }}>
                    <Button onClick={() => setRepoDlg(p => ({ ...p, open: false }))} disabled={repoDlg.saving} variant="outlined">Cancelar</Button>
                    <Button onClick={guardarRepositorio} disabled={repoDlg.saving} variant="contained"
                        style={{ background: "#1769aa", fontWeight: 700, color: "#fff" }}>
                        {repoDlg.saving ? "Guardando..." : "Guardar"}
                    </Button>
                </DialogActions>
            </Dialog>
            {/* Preview de escrito */}
            <Dialog
                open={previewOpen}
                onClose={() => { setPreviewOpen(false); setPreviewVinculado(null); }}
                maxWidth="xl"
                fullWidth
                keepMounted
                PaperProps={{
                    style: { width: "95vw", maxWidth: "95vw", borderRadius: 4 }
                }}
            >
                <DialogTitle style={{ background: "#5d7b9d", padding: 14 }}>
                    <span style={{ color: "#fff", fontWeight: 700 }}>{previewTitle || "Adjunto PDF"}</span>
                </DialogTitle>

                <DialogContent style={{ padding: 0, overflow: "hidden" }}>
                    {previewPdfUrl ? (
                        <div style={{ height: "82vh", minHeight: 420 }}>
                            <iframe
                                src={previewPdfUrl}
                                title={previewTitle || "PDF"}
                                style={{ width: "100%", height: "100%", border: 0 }}
                            />
                        </div>
                    ) : (
                        <div style={{
                            height: "82vh",
                            minHeight: 420,
                            overflow: "auto",
                            padding: "20px",
                            background: "#fff"
                        }}>
                            <div dangerouslySetInnerHTML={{ __html: previewHtml || '<p>Sin contenido</p>' }} />
                        </div>
                    )}
                </DialogContent>

                <DialogActions style={{ justifyContent: "space-between", padding: "10px 16px" }}>
                    <div style={{ display: "flex", gap: 8 }}>
                        {/* Vinculado imagen: solo botón Descargar Imagen */}
                        {previewVinculado && ["jpg", "jpeg", "png", "gif"].includes(previewVinculado.ext) ? (
                            <Button
                                variant="outlined"
                                startIcon={<GetAppIcon />}
                                onClick={async () => {
                                    try {
                                        await downloadVinculadoJWT(idExpediente, previewVinculado.id, previewTitle || "imagen");
                                    } catch {
                                        setSnackbar({ open: true, msg: "No se pudo descargar la imagen.", severity: "warning" });
                                    }
                                }}
                            >
                                Descargar Imagen
                            </Button>
                        ) : (
                            <>
                                <Button
                                    variant="outlined"
                                    startIcon={<PrintIcon />}
                                    onClick={() => {
                                        const iframe = document.createElement('iframe');
                                        iframe.style.cssText = 'position:fixed;left:-9999px;top:-9999px;width:0;height:0;';
                                        document.body.appendChild(iframe);
                                        const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
                                        if (previewPdfUrl) {
                                            iframe.src = previewPdfUrl;
                                            iframe.onload = () => { try { iframe.contentWindow.print(); } catch {} setTimeout(() => document.body.removeChild(iframe), 1000); };
                                        } else {
                                            iframeDoc.open();
                                            iframeDoc.write(`<html><head><title>${previewTitle || 'Escrito'}</title><style>@page{size:A4;margin:20mm 15mm;}body{font-family:Times New Roman,serif;font-size:12pt;margin:0;padding:0;}table{border-collapse:collapse;width:100%;margin:10px 0;}th,td{border:1px solid #999;padding:6px 8px;text-align:left;font-size:11pt;}th{background:#e6ecf0;font-weight:bold;}hr{border:none;border-top:1px solid #333;margin:10px 0;}p{margin:0 0 8px 0;}</style></head><body>${previewHtml || ''}</body></html>`);
                                            iframeDoc.close();
                                            setTimeout(() => { try { iframe.contentWindow.print(); } catch {} setTimeout(() => document.body.removeChild(iframe), 1000); }, 300);
                                        }
                                    }}
                                >
                                    Imprimir
                                </Button>
                                <Button
                                    variant="outlined"
                                    startIcon={descargandoPdf ? <CircularProgress size={18} /> : <GetAppIcon />}
                                    disabled={descargandoPdf}
                                    onClick={async () => {
                                        const title = (previewTitle || 'documento').replace(/[^a-zA-Z0-9áéíóúñ\s_-]/g, '').trim();
                                        // Vinculado PDF: descargar via fetch+blob
                                        if (previewVinculado && previewVinculado.ext === "pdf") {
                                            try {
                                                await downloadVinculadoJWT(idExpediente, previewVinculado.id, title || "documento");
                                            } catch {
                                                setSnackbar({ open: true, msg: "No se pudo descargar el PDF.", severity: "warning" });
                                            }
                                            return;
                                        }
                                        if (previewPdfUrl) {
                                            if (previewPdfUrl.startsWith('blob:')) {
                                                try {
                                                    const resp = await fetch(previewPdfUrl);
                                                    const blob = await resp.blob();
                                                    const url = URL.createObjectURL(blob);
                                                    const a = document.createElement('a');
                                                    a.href = url;
                                                    a.download = `${title}.pdf`;
                                                    document.body.appendChild(a);
                                                    a.click();
                                                    document.body.removeChild(a);
                                                    URL.revokeObjectURL(url);
                                                } catch {
                                                    window.open(previewPdfUrl, '_blank');
                                                }
                                            } else {
                                                const a = document.createElement('a');
                                                a.href = previewPdfUrl;
                                                a.download = `${title}.pdf`;
                                                document.body.appendChild(a);
                                                a.click();
                                                document.body.removeChild(a);
                                            }
                                        } else if (previewHtml) {
                                            setDescargandoPdf(true);
                                            try {
                                                await descargarHtmlComoPdf(previewHtml, { filename: title });
                                            } catch (err) {
                                                console.error('Error generando PDF:', err);
                                                setSnackbar({ open: true, msg: 'Error al generar PDF', severity: 'error' });
                                            } finally {
                                                setDescargandoPdf(false);
                                            }
                                        }
                                    }}
                                >
                                    {descargandoPdf ? 'Generando...' : 'Descargar PDF'}
                                </Button>
                            </>
                        )}
                    </div>
                    <div style={{ display: "flex", gap: 8 }}>
                        {previewPdfUrl && !previewVinculado && (
                            <Button
                                variant="outlined"
                                onClick={() => window.open(buildFileUrl(previewPdfUrl || ""), "_blank", "noopener")}
                            >
                                Abrir en pestaña nueva
                            </Button>
                        )}
                        <Button color="primary" variant="contained" onClick={() => { setPreviewOpen(false); setPreviewVinculado(null); }}>
                            Cerrar
                        </Button>
                    </div>
                </DialogActions>
            </Dialog>
            {/* Confirmación genérica */}
            <Dialog
                open={confirmDlg.open}
                onClose={() => handleConfirmClose(false)}
                maxWidth="xs"
                fullWidth
                keepMounted
                disableEnforceFocus
                disableAutoFocus
                style={{ zIndex: 1400 }}
                BackdropProps={{
                    style: {
                        zIndex: 1399,
                        backgroundColor: 'rgba(0, 0, 0, 0.7)' // ⭐ Backdrop más oscuro y definido
                    }
                }}
                PaperProps={{
                    style: {
                        zIndex: 1401, // ⭐ El paper del dialog debe estar sobre el backdrop
                        position: 'relative'
                    }
                }}
            >
                <DialogTitle style={{ fontWeight: 700, background: '#f5f5f5' }}>
                    {confirmDlg.title}
                </DialogTitle>
                <DialogContent dividers style={{ padding: '20px' }}>
                    <div style={{ whiteSpace: 'pre-wrap', fontSize: '14px' }}>
                        {confirmDlg.content}
                    </div>
                </DialogContent>
                <DialogActions style={{ padding: '12px 16px', background: '#fafafa' }}>
                    <Button
                        onClick={() => handleConfirmClose(false)}
                        variant="outlined"
                        color="secondary"
                    >
                        {confirmDlg.cancelText}
                    </Button>
                    <Button
                        onClick={() => handleConfirmClose(true)}
                        color="primary"
                        variant="contained"
                        autoFocus
                        style={{ fontWeight: 600 }}
                    >
                        {confirmDlg.confirmText}
                    </Button>
                </DialogActions>
            </Dialog>
            {/* Modal selección Carta Poder - OBSOLETO: ahora va directo a generar y enviar al fiscal */}
            {/* Notificaciones */}
            <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>
                <Alert severity={snackbar.severity}>{snackbar.msg}</Alert>
            </Snackbar>

            {/* Dialog: vinculado eliminado */}
            <Dialog open={vinculadoEliminadoOpen} onClose={() => setVinculadoEliminadoOpen(false)}>
                <DialogTitle sx={{ background: "#6B89AC", color: "#fff" }}>
                    Documento no disponible
                </DialogTitle>
                <DialogContent sx={{ mt: 2 }}>
                    <Typography>
                        El documento vinculado a este movimiento fue eliminado y ya no está disponible.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setVinculadoEliminadoOpen(false)}
                        variant="contained"
                        sx={{ borderRadius: 1, background: "#1567B7", textTransform: "none", fontWeight: 600 }}
                    >
                        Entendido
                    </Button>
                </DialogActions>
            </Dialog>
        </Paper>
    );
}

export default MovimientosTab;